window.Shopify = window.Shopify || {};
window.theme = window.theme || {};
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, SplitText);
const __deferToDomReady = (cb) => {
  if (typeof cb !== 'function') return;
  if (document.readyState === 'loading') {
    if (Array.isArray(window.ThemeDefer)) {
      window.ThemeDefer.push(cb);
    } else {
      document.addEventListener('DOMContentLoaded', cb, { once: true });
    }
    return;
  }
  cb();
};

try {
  const opts = Object.defineProperty({}, 'passive', {
    get() {
      theme.supportsPassive = true;
    },
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch {
  theme.supportsPassive = false;
}

try {
  document.querySelector(':focus-visible');
} catch {
  theme.focusVisiblePolyfill();
}

(() => {
  theme = {
    /* ----------------------------------------
      PUB/SUB
    ---------------------------------------- */
    PUB_SUB_EVENTS: {
      cartUpdate: 'cart-update',
      quantityUpdate: 'quantity-update',
      optionValueSelectionChange: 'option-value-selection-change',
      variantChange: 'variant-change',
      cartError: 'cart-error',
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      (theme.subscribers[eventName] ||= []).push(callback);
      return () => {
        theme.subscribers[eventName] = theme.subscribers[eventName].filter(cb => cb !== callback);
      };
    },

    publish: (eventName, data) => {
      theme.subscribers[eventName]?.forEach(cb => cb(data));
    },

    /* ----------------------------------------
      Focus Trapping
    ---------------------------------------- */
    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe, button:not(.reset__button)"
        )
      );
    },

    focusVisiblePolyfill: () => {
      const NAV_KEYS = new Set([
        'ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT',
        'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN',
      ]);

      let currentFocused = null;
      let usingMouse = false;

      window.addEventListener('keydown', e => {
        if (NAV_KEYS.has(e.code.toUpperCase())) usingMouse = false;
      }, theme.supportsPassive ? { passive: true } : false);

      window.addEventListener('mousedown', () => {
        usingMouse = true;
      }, theme.supportsPassive ? { passive: true } : false);

      window.addEventListener('focus', () => {
        currentFocused?.classList.remove('focused');
        if (usingMouse) return;
        currentFocused = document.activeElement;
        currentFocused.classList.add('focused');
      }, true);
    },

    trapFocusHandlers: null,

    trapFocus: (container, elementToFocus = container, preventSelection = false) => {
      const elements = theme.getFocusableElements(container);
      const visibleElements = elements.filter(el => !el.classList.contains('hidden'));

      if (!visibleElements.length) return;

      const first = visibleElements[0];
      const last = visibleElements[visibleElements.length - 1];

      theme.removeTrapFocus();

      theme.trapFocusHandlers = {
        focusin(e) {
          if (![container, first, last].includes(e.target)) return;
          document.addEventListener('keydown', theme.trapFocusHandlers.keydown);
        },
        focusout(e) {
          if (![container, first, last].includes(e.target)) return;
          document.removeEventListener('keydown', theme.trapFocusHandlers.keydown);
        },
        keydown(e) {
          if (e.code.toUpperCase() !== 'TAB') return;
          if (e.target === last && !e.shiftKey) {
            e.preventDefault();
            first.focus();
          }
          if ((e.target === container || e.target === first) && e.shiftKey) {
            e.preventDefault();
            last.focus();
          }
        }
      };

      document.addEventListener('focusin', theme.trapFocusHandlers.focusin);
      document.addEventListener('focusout', theme.trapFocusHandlers.focusout);

      elementToFocus.focus();

      if (
        !preventSelection &&
        elementToFocus.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },

    removeTrapFocus: (elementToFocus = null) => {
      if (!theme.trapFocusHandlers) return;
      const { focusin, focusout, keydown } = theme.trapFocusHandlers;
      document.removeEventListener('focusin', focusin);
      document.removeEventListener('focusout', focusout);
      document.removeEventListener('keydown', keydown);
      theme.trapFocusHandlers = null;
      elementToFocus?.focus();
    },

    onKeyUpEscape: (e) => {
      if (e.code.toUpperCase() !== 'ESCAPE') return;
      const openDetails = e.target.closest('details[open]');
      if (!openDetails) return;
      const summary = openDetails.querySelector('summary');
      openDetails.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
      summary.focus();
    },

    /* ----------------------------------------
      Utilities
    ---------------------------------------- */
    postMessageAll: (selector, message) => {
      document.querySelectorAll(selector).forEach(el => el.contentWindow?.postMessage(message, '*'));
    },

    pauseAllMedia: () => {
      theme.postMessageAll('.js-youtube', '{"event":"command","func":"pauseVideo","args":""}');
      theme.postMessageAll('.js-vimeo', '{"method":"pause"}');
      document.querySelectorAll('video').forEach(v => v.pause());
      document.querySelectorAll('product-model').forEach(m => m.modelViewerUI?.pause());
    },

    debounce: (fn, wait) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    },

    throttle: (fn, delay) => {
      let last = 0;
      return (...args) => {
        const now = Date.now();
        if (now - last < delay) return;
        last = now;
        fn(...args);
      };
    },

    fetchConfig: (type = 'json') => {
      return {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/${type}`,
        },
      };
    },

    initSectionVisible: ({ element, threshold = 0, callback }) => {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && typeof callback === 'function') {
              callback();
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: `0px 0px ${threshold}px 0px` }
      );
      observer.observe(element);
    },

    initWhenVisible: (callback, delay = 5000) => {
      if (typeof callback !== 'function') return () => {};

      const EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];
      const OPTS = { once: true, passive: true, capture: true };

      let fired = false;
      let timerId = 0;
      let idleId = 0;

      function run() {
        if (fired) return;
        fired = true;
        cleanup();

        if ('requestIdleCallback' in window) {
          idleId = requestIdleCallback(() => callback(), { timeout: 1500 });
        } else {
          setTimeout(callback, 0);
        }
      }

      function onVisibility() {
        if (document.visibilityState === 'visible') run();
      }

      function cleanup() {
        if (timerId) { clearTimeout(timerId); timerId = 0; }
        if (idleId && 'cancelIdleCallback' in window) { cancelIdleCallback(idleId); idleId = 0; }
        for (let i = EVENTS.length; i--;) {
          window.removeEventListener(EVENTS[i], run, OPTS);
        }
        document.removeEventListener('visibilitychange', onVisibility);
      }

      if (typeof delay === 'number' && delay >= 0) {
        timerId = setTimeout(run, delay);
      }

      for (let i = EVENTS.length; i--;) {
        window.addEventListener(EVENTS[i], run, OPTS);
      }
      document.addEventListener('visibilitychange', onVisibility, { passive: true });

      return cleanup;
    },

    config: {
      motionReduced: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      gsapEasePower2Out: "power2.out",
    },

    media: {
      isMobile: window.matchMedia("(max-width: 749px)").matches,
      isTablet: window.matchMedia("(max-width: 1024px)").matches,
      isDesktop: window.matchMedia("(min-width: 1025px)").matches,
      isDesktopLarge: window.matchMedia("(min-width: 1201px)").matches,
      isTouch: "ontouchstart" in window || navigator.maxTouchPoints > 0,
    },

    utils: {
      getAttr: (defaultVal, rawVal) => {
        if (typeof rawVal !== "string" || rawVal.trim() === "") return defaultVal;
        const type = typeof defaultVal;
        if (type === "boolean") return rawVal === "true";
        if (type === "number") return !isNaN(rawVal) ? +rawVal : defaultVal;
        if (type === "string") return isNaN(rawVal) ? rawVal : defaultVal;
        return defaultVal;
      },

      waitForFonts: () => {
        return new Promise((resolve) => {
          const checkFonts = () => {
            if (document.readyState === 'complete') {
              requestAnimationFrame(() => {
                setTimeout(resolve, 100);
              });
            } else {
              window.addEventListener('load', () => {
                requestAnimationFrame(() => {
                  setTimeout(resolve, 100);
                });
              });
            }
          };

          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => {
              requestAnimationFrame(() => {
                setTimeout(resolve, 50);
              });
            }).catch(() => {
              checkFonts();
            });
          } else {
            checkFonts();
          }
        });
      }
    }
  };

  Shopify.bind = function (fn, scope) {
    return function () {
      return fn.apply(scope, arguments);
    };
  };

  Shopify.setSelectorByValue = function (selector, value) {
    for (var i = 0, count = selector.options.length; i < count; i++) {
      var option = selector.options[i];
      if (value == option.value || value == option.innerHTML) {
        selector.selectedIndex = i;
        return i;
      }
    }
  };

  Shopify.addListener = function (target, eventName, callback) {
    target.addEventListener
      ? target.addEventListener(eventName, callback, false)
      : target.attachEvent('on' + eventName, callback);
  };

  Shopify.postLink = function (path, options) {
    options = options || {};
    var method = options['method'] || 'post';
    var params = options['parameters'] || {};

    var form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (var key in params) {
      var hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', params[key]);
      form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid);
    this.provinceEl = document.getElementById(province_domid);
    this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

    Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

    this.initCountry();
    this.initProvince();
  };

  Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
      var value = this.countryEl.getAttribute('data-default');
      Shopify.setSelectorByValue(this.countryEl, value);
      this.countryHandler();
    },

    initProvince: function () {
      var value = this.provinceEl.getAttribute('data-default');
      if (value && this.provinceEl.options.length > 0) {
        Shopify.setSelectorByValue(this.provinceEl, value);
      }
    },

    countryHandler: function (e) {
      var opt = this.countryEl.options[this.countryEl.selectedIndex];
      var raw = opt.getAttribute('data-provinces');
      var provinces = JSON.parse(raw);

      this.clearOptions(this.provinceEl);
      if (provinces && provinces.length == 0) {
        this.provinceContainer.style.display = 'none';
      } else {
        for (var i = 0; i < provinces.length; i++) {
          var opt = document.createElement('option');
          opt.value = provinces[i][0];
          opt.innerHTML = provinces[i][1];
          this.provinceEl.appendChild(opt);
        }

        this.provinceContainer.style.display = '';
        setLabelWidth();
      }
    },

    clearOptions: function (selector) {
      while (selector.firstChild) {
        selector.removeChild(selector.firstChild);
      }
    },

    setOptions: function (selector, values) {
      for (var i = 0, count = values.length; i < values.length; i++) {
        var opt = document.createElement('option');
        opt.value = values[i];
        opt.innerHTML = values[i];
        selector.appendChild(opt);
      }
    },
  };


  // Format money Function
  Shopify.formatMoney = function (cents, format) {
    if (typeof cents == "string") {
      cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || this.money_format;

    function defaultOption(opt, def) {
      return typeof opt == "undefined" ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ",");
      decimal = defaultOption(decimal, ".");
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ");
        break;
      case "amount_no_decimals_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 0, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }
    if (formatString.indexOf("with_comma_separator") !== -1) {
      return formatString.replace(placeholderRegex, value);
    } else {
      return formatString.replace(placeholderRegex, value);
    }
  };
})();

function checkFirstAction(e) {
  if (e.key?.toLowerCase() === 'tab') {
    if (document.body.classList.contains('using-tab')) return;
    document.body.classList.add('using-tab');
  }
}

window.addEventListener('keydown', checkFirstAction);
window.addEventListener('mousedown', () => {
  document.body?.classList.remove('using-tab');
});



class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

function getScrollbarWidth() {
  const width = window.innerWidth - document.documentElement.clientWidth;

  if (width > 17) return;
  document.documentElement.style.setProperty('--scrollbar-width', `${width}px`);
}

document.addEventListener('DOMContentLoaded', () => {
  getScrollbarWidth();

  window.addEventListener('resize', () => {
    getScrollbarWidth();
  });
});

class SectionId {
  static #separator = '__';

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  const details = summary.closest('details');
  const content = summary.nextElementSibling;

  summary.role = 'button';
  summary.ariaExpanded = details.hasAttribute('open');

  if (content?.id) summary.ariaControls = content.id;

  summary.addEventListener('click', () => {
    summary.ariaExpanded = !details.hasAttribute('open');
  });

  if (summary.closest('header-drawer')) return;

  details.addEventListener('keyup', theme.onKeyUpEscape);
});

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.buttons = this.querySelectorAll('button');

    this.input.addEventListener('change', () => this.validateQtyRules());
    this.buttons.forEach(btn => btn.addEventListener('click', e => this.handleClick(e)));
  }

  connectedCallback() {
    this.validateQtyRules();
    this.unsubscribe = theme.subscribe(theme.PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
  }

  disconnectedCallback() {
    this.unsubscribe?.();
  }

  handleClick(e) {
    e.preventDefault();
    const { input } = this;
    const prev = input.value;
    const { name } = e.target;

    if (name === 'plus') {
      if (parseInt(input.dataset.min) > parseInt(input.step) && input.value == 0) {
        input.value = input.dataset.min;
      } else {
        input.stepUp();
      }
    } else {
      input.stepDown();
    }

    if (prev !== input.value) input.dispatchEvent(this.changeEvent);
    if (input.dataset.min === prev && name === 'minus') input.value = parseInt(input.min);
  }

  validateQtyRules() {
    const { input } = this;
    const value = parseInt(input.value);

    const toggleDisabled = (selector, condition) => {
      const btn = this.querySelector(selector);
      btn?.classList.toggle('disabled', condition);
    };

    if (input.min) toggleDisabled(".quantity__button[name='minus']", value <= parseInt(input.min));
    if (input.max) toggleDisabled(".quantity__button[name='plus']", value >= parseInt(input.max));
  }
}
customElements.define('quantity-input', QuantityInput);

class ShortDescriptionBlock extends HTMLElement {
  constructor() {
    super();
    this._initialized = false;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const charLimitAttr = this.dataset.charLimit;
    const charLimit = Number.parseInt(charLimitAttr, 10) > 0 ? Number.parseInt(charLimitAttr, 10) : 300;
    const strings = window.accessibilityStrings || {};
    const moreText = this.dataset.moreText || strings.read_more || 'Read more';
    const lessText = this.dataset.lessText || strings.read_less || 'Read less';

    const placeholderEl = this.querySelector('[data-placeholder]');
    const sourceContentEl = this.querySelector('.short-description__source-content');
    if (!sourceContentEl) return;

    const sourceHtml = sourceContentEl.innerHTML;
    const totalText = (sourceContentEl.textContent || '').trim();

    const htmlToNodes = (html) => {
      const mount = document.createElement('div');
      mount.innerHTML = html;
      return Array.from(mount.childNodes);
    };

    /** Swap placeholder + hidden source for final nodes in one step (avoids layout jump). */
    const swapInContent = (...nodes) => {
      sourceContentEl.remove();
      if (placeholderEl) {
        placeholderEl.replaceWith(...nodes);
      } else {
        this.replaceChildren(...nodes);
      }
    };

    const mountFullContent = () => {
      swapInContent(...htmlToNodes(sourceHtml));
    };

    if (!totalText || totalText.length <= charLimit) {
      mountFullContent();
      return;
    }

    const sourceRoot = document.createElement('div');
    sourceRoot.innerHTML = sourceHtml;
    ShortDescriptionBlock.unwrapContainerNodes(sourceRoot);

    let remaining = charLimit;

    const visibleTextElement = document.createElement('div');
    visibleTextElement.className = 'short-description__visible';

    const restTextElement = document.createElement('div');
    restTextElement.className = 'short-description__rest';

    const containerTags = ['DIV', 'SECTION', 'ARTICLE'];
    // P is splittable so a long opening paragraph still gets read more / read less
    const nonSplittableTags = [
      'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
      'UL', 'OL', 'LI',
      'BLOCKQUOTE',
      'TABLE',
      'HR'
    ];

    const getTextLength = (node) => (node.textContent || '').length;
    const punctuationBoundaryRegex = /[.,!?;:)\]}'"`]/;
    const whitespaceRegex = /\s/;

    const getWordBoundarySplitIndex = (text, maxChars) => {
      if (text.length <= maxChars) return text.length;

      for (let i = maxChars; i > 0; i -= 1) {
        const prevChar = text[i - 1] || '';
        if (whitespaceRegex.test(prevChar)) {
          return i - 1;
        }
        if (punctuationBoundaryRegex.test(prevChar)) {
          return i;
        }
      }

      // No suitable boundary found near limit, fallback to hard cut.
      return maxChars;
    };

    const splitChildren = (fromParent, visibleParent, hiddenParent) => {
      Array.from(fromParent.childNodes).forEach((node) => {
        if (remaining <= 0) {
          hiddenParent.appendChild(node.cloneNode(true));
          return;
        }

        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          if (!text.trim()) return;

          if (text.length <= remaining) {
            visibleParent.appendChild(document.createTextNode(text));
            remaining -= text.length;
          } else {
            const splitIndex = getWordBoundarySplitIndex(text, remaining);
            const visiblePart = text.slice(0, splitIndex).replace(/\s+$/g, '');
            const hiddenPart = text.slice(splitIndex).replace(/^\s+/g, '');
            if (visiblePart) {
              visibleParent.appendChild(document.createTextNode(visiblePart));
            }
            if (hiddenPart) {
              hiddenParent.appendChild(document.createTextNode(hiddenPart));
            }
            remaining = 0;
          }
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (containerTags.includes(node.tagName)) {
          splitChildren(node, visibleParent, hiddenParent);
          return;
        }

        const textLength = getTextLength(node);
        const isNonSplittable = nonSplittableTags.includes(node.tagName);

        if (textLength <= remaining) {
          visibleParent.appendChild(node.cloneNode(true));
          remaining -= textLength;
          return;
        }

        if (isNonSplittable) {
          hiddenParent.appendChild(node.cloneNode(true));
          remaining = 0;
          return;
        }

        const visibleClone = node.cloneNode(false);
        const hiddenClone = node.cloneNode(false);

        splitChildren(node, visibleClone, hiddenClone);

        if (visibleClone.childNodes.length) {
          visibleParent.appendChild(visibleClone);
        }

        if (hiddenClone.childNodes.length) {
          hiddenParent.appendChild(hiddenClone);
        }
      });
    };

    splitChildren(sourceRoot, visibleTextElement, restTextElement);

    if (!restTextElement.hasChildNodes()) {
      mountFullContent();
      return;
    }

    if (!visibleTextElement.hasChildNodes()) {
      remaining = charLimit;
      restTextElement.replaceChildren();
      splitChildren(sourceRoot, visibleTextElement, restTextElement);
    }

    if (!visibleTextElement.hasChildNodes()) {
      mountFullContent();
      return;
    }

    const ellipsisTextElement = document.createElement('span');
    ellipsisTextElement.className = 'short-description__ellipsis';
    ellipsisTextElement.textContent = ' ... ';

    const moreButton = document.createElement('button');
    moreButton.type = 'button';
    moreButton.className = 'short-description__toggle--more link p-0 full-unstyled-link';
    moreButton.appendChild(document.createTextNode(moreText));

    const hiddenWrapper = document.createElement('div');
    hiddenWrapper.className = 'short-description__hidden';
    hiddenWrapper.hidden = true;

    const lessButton = document.createElement('button');
    lessButton.type = 'button';
    lessButton.className = 'short-description__toggle--less link p-0 full-unstyled-link';
    lessButton.appendChild(document.createTextNode(lessText));

    hiddenWrapper.append(restTextElement, lessButton);

    swapInContent(visibleTextElement, ellipsisTextElement, moreButton, hiddenWrapper);

    moreButton.addEventListener('click', (event) => {
      event.preventDefault();
      ellipsisTextElement.hidden = true;
      moreButton.hidden = true;
      hiddenWrapper.hidden = false;
    });

    lessButton.addEventListener('click', (event) => {
      event.preventDefault();
      hiddenWrapper.hidden = true;
      ellipsisTextElement.hidden = false;
      moreButton.hidden = false;
    });
  }

  static unwrapContainerNodes(root) {
    while (
      root.childNodes.length === 1 &&
      root.firstChild?.nodeType === Node.ELEMENT_NODE &&
      ['DIV', 'SECTION', 'ARTICLE'].includes(root.firstChild.tagName)
    ) {
      const wrapper = root.firstChild;
      while (wrapper.firstChild) {
        root.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    }
  }
}
customElements.define('short-description-block', ShortDescriptionBlock);

class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const sticky = this.getAttribute('sticky');
    const isSidebarDesktop =
      this.dataset.headerType === 'sidebar' && matchMedia('(min-width: 1025px)').matches;

    if (!isSidebarDesktop) {
      this.header = this.closest('.section-header') || this;
      if (!this.header && sticky === 'never') return;

      if (sticky === 'always') {
        this.header.classList.add('section-header-style:sticky');
        return;
      }

      if (sticky !== 'scroll-up') return;
    }

    if (Shopify.designMode) {
      this.bootstrap();
      return;
    }

    this._cleanupDefer = theme.initWhenVisible(() => this.bootstrap(), 2500);
  }

  bootstrap() {
    if (this._bootstrapped) return;
    this._bootstrapped = true;

    if (this.dataset.headerType === 'sidebar' && matchMedia('(min-width: 1025px)').matches) {
      this.cacheElements();
      this.initSidebarMenu();
      this.initSidebarLogo();
      return;
    }

    if (!this.header) {
      this.header = this.closest('.section-header') || this;
    }

    if (this.getAttribute('sticky') === 'scroll-up') {
      this.initStandardMenu();
    }
  }

  disconnectedCallback() {
    this._cleanupDefer?.();
    this._cleanupDefer = null;

    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this._onResize);
    this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);

    document.removeEventListener("keydown",this.onKeydown);

    this.mediaQuery?.removeEventListener('change',this.setHeaderHeight);
  }

  cacheElements() {
    this.toggleBtn = this.querySelector(".sidebar-button");
    this.sidebar = this.querySelector(".sidebar-menu");
    this.hamburger = this.querySelector(".icon-hamburger");
    this.sidebarLogo = this.querySelector(".sidebar-logo");
  }

  get isMenuOpen() {
    return this.sidebar?.classList.contains("active");
  }

  animateOpen(el, duration = 0.45) {
    gsap.to(el, {
      maxHeight: "100vh",
      opacity: 1,
      duration,
      ease: "power3.out"
    });
  }

  animateClose(el, duration = 0.35) {
    gsap.to(el, {
      maxHeight: 0,
      opacity: 0,
      duration,
      ease: "power3.in"
    });
  }

  initSidebarMenu() {
    if (!this.toggleBtn || !this.sidebar) return;

    const openMenu = () => {
      this.animateOpen(this.sidebar);
      this.sidebar.classList.add("active");
      this.sidebar.setAttribute("aria-hidden", "false");
      this.hamburger?.classList.add("active");
      this.showLogo();
    };

    const closeMenu = () => {
      this.animateClose(this.sidebar);
      this.sidebar.classList.remove("active");
      this.sidebar.setAttribute("aria-hidden", "true");
      this.hamburger?.classList.remove("active");
      this.hideLogo();
    };

    this.toggleBtn.addEventListener("click", () =>
      this.isMenuOpen ? closeMenu() : openMenu()
    );

    this.onKeydown = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("keydown", this.onKeydown);

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:select", openMenu);
      document.addEventListener("shopify:section:deselect", closeMenu);
    }
  }

  initSidebarLogo() {
    if (!this.sidebarLogo || !this.sidebarLogo.classList.contains("has-menu")) return;

    this.addEventListener("mouseenter", () => {
      this.showLogo();
    });

    this.addEventListener("mouseleave", () => {
      if (this.isMenuOpen) return;
      this.hideLogo();
    });

    this.sidebarLogo.querySelector("a").addEventListener("focus", () => {
      this.showLogo();
    });
  }

  showLogo() {
    if (!this.sidebarLogo) return;
    this.animateOpen(this.sidebarLogo);
    this.sidebarLogo.classList.add("active");
  }

  hideLogo() {
    if (!this.sidebarLogo || this.isMenuOpen) return;
    this.animateClose(this.sidebarLogo);
    this.sidebarLogo.classList.remove("active");
  }

  initStandardMenu() {
    this.currentScrollTop = 0;
    this.preventReveal = false;
    this.headerBounds = {};

    this.setHeaderHeight = () => {
      if(this.classList.contains('header-style--standard')) {
        document.documentElement.style.setProperty(
          '--header-height',
          `${this.offsetHeight}px`
        );
      } else {
        document.documentElement.style.setProperty(
          '--header-height',
          `${this.header.offsetHeight}px`
        );
      }
    };

    this.setHeaderHeight();
    this.mediaQuery = window.matchMedia('(max-width: 1025px)');
    this.mediaQuery.addEventListener('change', this.setHeaderHeight);

    this.onScrollHandler = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });

    this.createHeaderObserver();

    this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
    this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);

    if (Shopify.designMode) {
      this.handleDesignMode();
    }
  }

  createHeaderObserver() {
    const observer = new IntersectionObserver((entries, obs) => {
      this.headerBounds = entries[0].intersectionRect;
      obs.disconnect();
    });

    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    const pastHeader = scrollTop > this.headerBounds.bottom;

    if (scrollTop > this.currentScrollTop && pastHeader) {
      if (!this.preventHide) this.hide();
    } else if (scrollTop < this.currentScrollTop && pastHeader) {
      if (!this.preventReveal) {
        this.reveal();
      } else {
        clearTimeout(this.isScrolling);
        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        this.hide();
      }
    } else if (scrollTop <= this.headerBounds.top) {
      this.reset();
    }

    this.currentScrollTop = scrollTop;
  }

  hide() {
    this.header.classList.add(
      'section-header-style:hidden',
      'section-header-style:sticky'
    );
  }

  reveal() {
    this.header.classList.add(
      'section-header-style:sticky',
      'animate'
    );
    this.header.classList.remove('section-header-style:hidden');
  }

  reset() {
    this.header.classList.remove(
      'section-header-style:hidden',
      'section-header-style:sticky',
      'animate'
    );
  }

  handleDesignMode() {
    document.addEventListener('shopify:section:select', () => {
      this.header.classList.add('section-header-style:sticky');
    });

    document.addEventListener('shopify:section:deselect', () => {
      this.reset();
    });
  }
}
customElements.define("header-component", HeaderComponent);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector, .selector__close-button, .country-filter__reset-button, .slider-control, .swiper-button-prev, .swiper-button-next)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      theme.trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    theme.trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    theme.removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (detailsElement) this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    theme.removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          theme.trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    theme.trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}
customElements.define("header-drawer", HeaderDrawer);

class ModalComponent extends HTMLElement {
  constructor() {
    super();

    window.__modalRegistry = window.__modalRegistry || new Map();

    if (this.id) {
      const existing = window.__modalRegistry.get(this.id);
      const isExistingAlive = existing && document.contains(existing);

      if (isExistingAlive && existing !== this && !Shopify.designMode) {
        this.remove();
        return;
      }

      if (!isExistingAlive) {
        window.__modalRegistry.set(this.id, this);
        this.__isPrimary = true;
      } else {
        this.__isPrimary = existing === this;
      }
    }

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('modal-overlay')?.addEventListener('click', () => this.close());

    // Drawer function
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.close.bind(this));

    const modalId = `#${this.id}`;
    const toggleButtons = document.querySelectorAll(`[data-drawer-toggle="${modalId}"]`);

    toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.show(button);
      });
    });

    // Shopify Design Mode
    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => (this.isOpen = true));
      this.addEventListener('shopify:block:deselect', () => (this.isOpen = false));
    }
  }

  connectedCallback() {
    if (this.id && !Shopify.designMode) {
      const firstMatched = document.querySelector(`modal-component#${this.id}`);
      if (firstMatched && firstMatched !== this) {
        this.remove();
        return;
      }
    }

    if (this.dataset.moved == 'true') {
      this.dataset.moved = false;
      // Defer moving until next frame so Liquid-rendered children
      // (e.g. page.content HTML) are fully parsed before relocation.
      requestAnimationFrame(() => {
        if (!this.isConnected || !document.body || this.parentNode === document.body) return;
        document.body.appendChild(this);
      });
    }
  }

  disconnectedCallback() {
    if (!this.id) return;

    const reg = window.__modalRegistry;
    if (!reg) return;

    if (reg.get(this.id) === this) {
      reg.delete(this.id);

      const next = document.querySelector(`modal-component#${this.id}`);
      if (next) {
        reg.set(this.id, next);
        next.__isPrimary = true;
      }
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-hidden', newValue === '' ? 'false' : 'true');
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      if (this.isConnected) {
        this._animate(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    this.setAttribute('aria-hidden', value ? 'false' : 'true');
  }

  async show(opener, preventClose = false) {
    const reg = window.__modalRegistry;
    if (reg) {
      for (const [id, modalInstance] of reg) {
        if (modalInstance !== this && modalInstance.isOpen && !preventClose) {
          modalInstance.close();
        }
      }
    }

    if (this.id === 'Modal-Drawer-Menu') {
      const cartButton = document.querySelector('#Modal-Drawer-Menu #cart-icon-bubble');

      if (cartButton) {
        cartButton.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      }
    }

    this.openedBy = opener;
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  async _animate(open) {
    const overlay = this.querySelector('.modal-component__overlay');
    const header = this.querySelector('.modal-component__header');
    const content = this.querySelector('.modal-component__content');

    const runLeftItemsEffect = (isOpen) => {
      const animateLeftItems = this.querySelectorAll('.animate-fade--left');
      if (!animateLeftItems.length) return;

      gsap.killTweensOf(animateLeftItems);

      if (isOpen) {
        gsap.fromTo(
          animateLeftItems,
          { opacity: 0, x: -12 },
          {
            opacity: 1,
            x: 0,
            duration: 0.35,
            ease: 'power3.out',
            stagger: 0.1,
            delay: 0.2,
            onComplete: () => {
              gsap.set(animateLeftItems, { clearProps: 'opacity,transform' });
            },
          }
        );
      } else {
        gsap.to(animateLeftItems, {
          opacity: 0,
          x: -8,
          duration: 0.2,
          ease: 'power2.in',
        });
      }
    };

    if (open) {
      this.setAttribute('open', '');
      overlay.classList.remove('exitFade');
      overlay.classList.add('openFade');
      this.trapFocus(open);
      document.body.classList.add('overflow-hidden');

      await gsap
        .timeline()
        .to(content, {
          opacity: 1,
          transform: 'translateY(0)',
          duration: 0.3,
          ease: 'cubic-bezier(0.7, 0, 0.3, 1)',
          onComplete: () => runLeftItemsEffect(true),
        })
        .to(header, {
          opacity: 1,
          transform: 'translateY(0)',
          duration: 0.3,
          ease: 'cubic-bezier(0.7, 0, 0.3, 1)',
        })
        .finished;
    } else {
      await gsap
        .timeline()
        .to(header, {
          opacity: 0,
          transform: 'translateY(-0.5rem)',
          duration: 0.2,
          ease: 'cubic-bezier(0.7, 0, 0.3, 1)',
        })
        .to(content, {
          opacity: 0,
          transform: 'translateY(1rem)',
          duration: 0.2,
          ease: 'cubic-bezier(0.7, 0, 0.3, 1)',
          // Run after content close animation finishes.
          onComplete: () => runLeftItemsEffect(false),
        })
        .finished;

      this.removeAttribute('open');
      overlay.classList.remove('openFade');
      overlay.classList.add('exitFade');
      theme.removeTrapFocus(this.openedBy);
      // Fallback: restore focus to the opener if not handled by removeTrapFocus
      if (this.openedBy && document.activeElement !== this.openedBy) {
        this.openedBy.focus();
      }
      document.body.classList.remove('overflow-hidden');
    }
  }

  trapFocus(open) {
    this.addEventListener('transitionend', () => {
      if (!this.hasAttribute('open')) return;
      const container = this.querySelector('.modal-component__wrapper');

      // Fallback to modal element if wrapper not found
      const safeContainer = container || this;
      const focusEl = this.querySelector('.button-close');
      const firstInput = container?.querySelector('.first-input-focus');

      if (firstInput) {
        theme.trapFocus(safeContainer, firstInput);
        firstInput.focus();
      } else {
        theme.trapFocus(safeContainer, focusEl);
        focusEl.focus();
      }
    }, { once: true });
  }
}
if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.isPlaying = false;
    this._abortController = new AbortController();
  }

  connectedCallback() {
    const signal = this._abortController.signal;

    // bind buttons
    this.playButton = this.querySelector('[ref="deferredMediaPlayButton"]');
    this.toggleButton = this.querySelector('[ref="toggleMediaButton"]');

    if (this.playButton) {
      this.playButton.addEventListener(
        "click",
        () => this.showDeferredMedia(),
        { signal }
      );
    }
    if (this.toggleButton) {
      this.toggleButton.addEventListener("click", () => this.toggleMedia(), {
        signal,
      });
    }

    document.addEventListener(
      "media:started-playing",
      this.pauseMedia.bind(this),
      { signal }
    );
    window.addEventListener("dialog:close", this.pauseMedia.bind(this), {
      signal,
    });
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  updatePlayPauseHint(isPlaying) {
    if (!this.toggleButton) return;
    this.toggleButton.classList.remove("hidden");

    const playIcon = this.toggleButton.querySelector(".icon-play");
    const pauseIcon = this.toggleButton.querySelector(".icon-pause");

    if (playIcon) playIcon.classList.toggle("hidden", isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle("hidden", !isPlaying);
  }

  showDeferredMedia() {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  loadContent(focus = true) {
    if (this.hasAttribute("data-media-loaded")) return;

    this.setAttribute("data-media-loaded", "true");

    const template = this.querySelector("template");
    const content = template?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.playButton?.classList.add("deferred-media__playing");

    if (
      content instanceof HTMLVideoElement &&
      content.getAttribute("autoplay")
    ) {
      content.play().catch(() => {}); // force autoplay
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*"
      );
    } else {
      this.querySelector("video")?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"pauseVideo","args":""}'
          : '{"method":"pause"}',
        "*"
      );
    } else {
      this.querySelector("video")?.pause();
    }

    this.isPlaying = false;
    if (this.hasAttribute("data-media-loaded")) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}
customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  static _initQueue = [];
  static _isProcessingQueue = false;

  constructor() {
    super();
    this.swiper = null;
    this.thumbnailSwiper = null;
    this._paginationTypeApplied = null;
    this._paginationMql = null;
    this._onPaginationMqChange = null;
    this._initObserver = null;
    this._initRaf = null;
    this._focusRaf = null;
    this._isQueuedForInit = false;
  }

  connectedCallback() {
    if (!this.swiper) {
      this._applyPreInitLayout();
      this._setupDeferredInit();
    }

    // Responsive pagination: desktop > 1024px, mobile <= 1024px
    if (!this._paginationMql && (this.dataset.paginationTypeDesktop || this.dataset.paginationTypeMobile)) {
      this._paginationMql = window.matchMedia("(min-width: 1025px)");
      this._onPaginationMqChange = () => this._handleResponsivePaginationChange();
      try {
        this._paginationMql.addEventListener("change", this._onPaginationMqChange);
      } catch {
        // Safari fallback
        this._paginationMql.addListener(this._onPaginationMqChange);
      }
    }
  }

  disconnectedCallback() {
    this._isQueuedForInit = false;

    if (this._initObserver) {
      this._initObserver.disconnect();
      this._initObserver = null;
    }
    if (this._initRaf) {
      cancelAnimationFrame(this._initRaf);
      this._initRaf = null;
    }
    if (this._focusRaf) {
      cancelAnimationFrame(this._focusRaf);
      this._focusRaf = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.thumbnailSwiper) {
      this.thumbnailSwiper.destroy(true, true);
      this.thumbnailSwiper = null;
    }

    if (this._paginationMql && this._onPaginationMqChange) {
      try {
        this._paginationMql.removeEventListener("change", this._onPaginationMqChange);
      } catch {
        this._paginationMql.removeListener(this._onPaginationMqChange);
      }
    }
    this._paginationMql = null;
    this._onPaginationMqChange = null;
  }

  _setupDeferredInit() {
    if (this.swiper || this._initObserver || this._initRaf) return;
    if (this.dataset.lazyInit === "false") {
      this._queueInit("high");
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight || 0;
    const nearViewportPx = parseInt(this.dataset.lazyInitNearViewport || 600, 10);
    const isNearViewport =
      rect.bottom >= -nearViewportPx &&
      rect.top <= viewportHeight + nearViewportPx;

    // If slider is already visible (or very close), initialize immediately
    // to avoid one-column flash before Swiper applies layout.
    if (isNearViewport) {
      this._queueInit("normal");
      return;
    }

    const rootMargin = this.dataset.lazyInitMargin || "900px 0px";
    this._initObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        this._initObserver?.disconnect();
        this._initObserver = null;
        this._queueInit("low");
      },
      { rootMargin }
    );
    this._initObserver.observe(this);
  }

  _queueInit(priority = "normal") {
    if (this.swiper || this._isQueuedForInit) return;
    this._isQueuedForInit = true;
    SliderComponent._initQueue.push({ instance: this, priority });
    SliderComponent._processInitQueue();
  }

  static _processInitQueue() {
    if (SliderComponent._isProcessingQueue) return;
    SliderComponent._isProcessingQueue = true;

    const rank = { high: 0, normal: 1, low: 2 };
    const drain = (deadline) => {
      SliderComponent._initQueue.sort(
        (a, b) => (rank[a.priority] ?? 1) - (rank[b.priority] ?? 1)
      );

      let didWork = false;
      while (SliderComponent._initQueue.length) {
        if (
          deadline &&
          typeof deadline.timeRemaining === "function" &&
          deadline.timeRemaining() < 8 &&
          didWork
        ) {
          break;
        }

        const next = SliderComponent._initQueue.shift();
        const instance = next?.instance;
        if (!instance || !instance.isConnected || instance.swiper) continue;

        instance._isQueuedForInit = false;
        instance._initNow();
        didWork = true;

        if (!deadline) break;
      }

      if (SliderComponent._initQueue.length) {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(drain, { timeout: 250 });
        } else {
          requestAnimationFrame(() => drain());
        }
      } else {
        SliderComponent._isProcessingQueue = false;
      }
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(drain, { timeout: 200 });
    } else {
      requestAnimationFrame(() => drain());
    }
  }

  _initNow() {
    if (this.swiper) return;
    this.initThumbnail();
    this.initSlider();
    this._clearPreInitLayout();
  }

  _getPreInitSlidesPerView() {
    const toFloat = (v, def) => parseFloat(v ?? def);
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    const isMegaMenuFeaturedProducts = Boolean(
      this.closest(".mega-menu__featured-products")
    );

    if (isMegaMenuFeaturedProducts) {
      if (width >= 1440) return toFloat(this.dataset.itemsDesktop, 1);
      if (width >= 1025) {
        return toFloat(this.dataset.itemsTablet || this.dataset.itemsDesktop, 1);
      }
      return toFloat(this.dataset.itemsMobile, 1);
    }

    if (width >= 1440) return toFloat(this.dataset.itemsDesktop, 1);
    if (width >= 750) {
      return toFloat(this.dataset.itemsTablet || this.dataset.itemsDesktop, 1);
    }
    return toFloat(this.dataset.itemsMobile, 1);
  }

  _getPreInitSpaceBetween() {
    const toInt = (v, def) => parseInt(v ?? def, 10);
    const width = window.innerWidth || document.documentElement.clientWidth || 0;
    if (width >= 1440) return toInt(this.dataset.spaceBetween, 20);
    if (width >= 750) return toInt(this.dataset.spaceBetweenTablet, toInt(this.dataset.spaceBetweenMobile, 20));
    return toInt(this.dataset.spaceBetweenMobile, 20);
  }

  _applyPreInitLayout() {
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) return;
    const slides = wrapper.querySelectorAll(".swiper-slide");
    if (!slides.length) return;

    const cols = Math.max(1, this._getPreInitSlidesPerView());
    const space = Math.max(0, this._getPreInitSpaceBetween());

    wrapper.style.display = "flex";
    wrapper.style.columnGap = `${space}px`;
    const basis = `calc((100% - ${(cols - 1) * space}px) / ${cols})`;
    slides.forEach((slide) => {
      slide.style.flex = `0 0 ${basis}`;
      slide.style.maxWidth = basis;
    });
  }

  _clearPreInitLayout() {
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) return;
    const slides = wrapper.querySelectorAll(".swiper-slide");

    wrapper.style.removeProperty("display");
    wrapper.style.removeProperty("column-gap");
    slides.forEach((slide) => {
      slide.style.removeProperty("flex");
      slide.style.removeProperty("max-width");
    });
  }

  _isDesktopPagination() {
    return window.matchMedia("(min-width: 1025px)").matches;
  }

  _getResponsivePaginationType() {
    const desktop = this.dataset.paginationTypeDesktop;
    const mobile = this.dataset.paginationTypeMobile;

    if (!desktop && !mobile) return this.dataset.paginationType || null;

    const isDesktop = this._isDesktopPagination();
    return (isDesktop ? desktop : mobile) || this.dataset.paginationType || null;
  }

  _applyPaginationVisibility(type) {
    const el = this.querySelector(".swiper-pagination");
    if (!el) return;
    el.style.display = type && type !== "none" ? "" : "none";
  }

  _handleResponsivePaginationChange() {
    const nextType = this._getResponsivePaginationType();
    if (nextType === this._paginationTypeApplied) return;

    // Preserve position on re-init
    const currentIndex = this.swiper?.realIndex ?? this.swiper?.activeIndex ?? 0;
    this.dataset.initialSlide = String(Math.max(0, currentIndex));

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.initSlider();
  }

  initThumbnail() {
    const thumbnailEl = document.querySelector(
      `slider-thumbnail[target="${this.id}"]`
    );
    if (!thumbnailEl) return;

    if (this.dataset.loop === "true") {
      thumbnailEl.dataset.loop = "true";
    }

    const wrapper = thumbnailEl.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    const thumbnailSlides = thumbnailEl.querySelectorAll('.swiper-slide').length;
    const thumbnailSlidesPerView = parseFloat(thumbnailEl.dataset.itemsMobile || 4);

    const thumbnailMaxSlidesPerView = this.getMaxSlidesPerView({
      itemsMobile: thumbnailEl.dataset.itemsMobile || 4,
      itemsTablet: thumbnailEl.dataset.itemsTablet || 4,
      itemsDesktop: thumbnailEl.dataset.itemsDesktop || 6
    });

    const thumbnailHasEnoughSlides = thumbnailSlides > thumbnailMaxSlidesPerView;

    if (thumbnailEl.dataset.type === "slideshow-content") {
      this.thumbnailSwiper = new Swiper(thumbnailEl, {
        slidesPerView: 1,
        loop: thumbnailEl.dataset.loop === "true" && thumbnailSlides > 1,
        watchSlidesProgress: true,
        allowTouchMove: false,
        effect: "fade",
      });

      return;
    }

    this.thumbnailSwiper = new Swiper(thumbnailEl, {
      loop: thumbnailEl.dataset.loop === "true" && thumbnailHasEnoughSlides,
      spaceBetween: parseInt(thumbnailEl.dataset.spaceBetween || 10),
      slidesPerView: thumbnailSlidesPerView,
      freeMode: thumbnailEl.dataset.freeMode === "true",
      watchSlidesProgress: true,

      breakpoints: thumbnailEl.dataset.breakpoints
        ? JSON.parse(thumbnailEl.dataset.breakpoints)
        : {
            768: {
              slidesPerView: parseFloat(thumbnailEl.dataset.itemsTablet || 4),
            },
            1024: {
              slidesPerView: parseFloat(thumbnailEl.dataset.itemsDesktop || 6),
            },
          },
    });
  }

  getMaxSlidesPerView(dataset) {
    return Math.max(
      parseFloat(dataset.itemsMobile || 1),
      parseFloat(dataset.itemsTablet || dataset.itemsMobile || 1),
      parseFloat(dataset.itemsDesktop || dataset.itemsTablet || dataset.itemsMobile || 1)
    );
  }

  _isMobile() {
    return !theme.media.isDesktop;
  }

  getConfigFromDataset() {
    const dataset = this.dataset;

    const slides = this.querySelectorAll('.swiper-slide').length;
    const maxSlidesPerView = this.getMaxSlidesPerView(dataset);
    const hasEnoughSlides = slides > maxSlidesPerView;
    let paginationType = this._getResponsivePaginationType();

    if (this._isMobile() && (!paginationType || paginationType === "none")) {
      paginationType = "bullets";
    }

    // Track which pagination type is currently applied (for responsive switching)
    this._paginationTypeApplied = paginationType;
    this._applyPaginationVisibility(paginationType);

    const toInt = (v, def) => parseInt(v ?? def);
    const toFloat = (v, def) => parseFloat(v ?? def);
    const toBool = (v) => v === "true";

    const spaceMobile = toInt(dataset.spaceBetweenMobile, 20);
    const spaceTablet = toInt(dataset.spaceBetweenTablet, spaceMobile);
    const spaceDesktop = dataset.spaceBetween;

    const coverflowBase = {
      rotate: 50,
      stretch: 0,
      depth: 100,
      modifier: 1,
      slideShadows: true,
    };

    const coverflowDesktop =
    dataset.effect === "coverflow"
      ? {
          rotate: 0,
          stretch: 74,
          depth: 100,
          modifier: 1,
          slideShadows: false,
          scale: 0.8,
        }
      : coverflowBase;

    const isMegaMenuFeaturedProducts = Boolean(
      this.closest(".mega-menu__featured-products")
    );

    const defaultBreakpoints = isMegaMenuFeaturedProducts
      ? {
          1025: {
            slidesPerView: toFloat(dataset.itemsTablet || dataset.itemsDesktop, 1),
            spaceBetween: spaceTablet,
            coverflowEffect: coverflowBase,
          },
          1440: {
            slidesPerView: toFloat(dataset.itemsDesktop, 1),
            spaceBetween: spaceDesktop,
            coverflowEffect: coverflowDesktop,
          },
        }
      : {
          750: {
            slidesPerView: toFloat(dataset.itemsTablet || dataset.itemsDesktop, 1),
            spaceBetween: spaceTablet,
            coverflowEffect: coverflowBase,
          },
          1440: {
            slidesPerView: toFloat(dataset.itemsDesktop, 1),
            spaceBetween: spaceDesktop,
            coverflowEffect: coverflowDesktop,
          },
        };

    return {
      initialSlide: toInt(dataset.initialSlide, 0),
      slidesPerView: toFloat(dataset.itemsMobile, 1),
      spaceBetween: spaceMobile,
      effect: dataset.effect || null,
      grabCursor: toBool(dataset.grabCursor),
      fadeEffect:
        dataset.effect === "fade"
          ? {
              crossFade: true,
            }
          : null,
      coverflowEffect: coverflowBase,
      nested: toBool(dataset.nested),
      loop: dataset.loop === "true" && hasEnoughSlides,
      centeredSlides: toBool(dataset.centeredSlides),
      speed: toInt(dataset.slideSpeed, 500),
      allowTouchMove: toBool(dataset.allowTouchMove),
      parallax: toBool(dataset.parallax),
      autoHeight: toBool(dataset.autoHeight),
      watchSlidesProgress: toBool(dataset.watchSlidesProgress),

      autoplay:
        toBool(dataset.autoplay)
          ? {
              delay: toInt(dataset.autoplaySpeed, 3000),
              disableOnInteraction: false,
              pauseOnMouseEnter: toBool(dataset.mediaGallery) ? false : true,
              reverseDirection: toBool(dataset.reverseDirection),
            }
          : false,

      pagination:
        paginationType && paginationType !== "none"
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              dynamicBullets: toBool(dataset.dynamicBullets),
              type: paginationType || "bullets",
            }
          : false,

      navigation:
        toBool(dataset.arrows) && !this._isMobile()
          ? {
              nextEl: this.querySelector(".swiper-button-next"),
              prevEl: this.querySelector(".swiper-button-prev"),
            }
          : false,

      breakpoints: dataset.breakpoints
        ? JSON.parse(dataset.breakpoints)
        : defaultBreakpoints,

      thumbs: this.thumbnailSwiper ? { swiper: this.thumbnailSwiper } : false,
    };
  }

  initSlider() {
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    const config = this.getConfigFromDataset();
    config.watchSlidesProgress = true;
    config.resizeObserver = false;
    this.swiper = new Swiper(this, config);

    if (this.dataset.parallax === "true") {
      const getMedia = (slide) =>
        slide?.querySelector('img, video, .slide__image');

      this.swiper.on('init', () => {
        const media = getMedia(
          this.querySelector('.swiper-slide-active')
        );

        if (!media) return;

        gsap.set(media, {
          scale: 1.25,
        });

        gsap.to(media, {
          scale: 1,
          duration: 1.25,
          ease: 'power3.out',
        });
      });

      this.swiper.on('slideChangeTransitionStart', () => {
        const activeSlide = this.querySelector('.swiper-slide-active');
        const media = getMedia(activeSlide);

        if (!media) return;

        gsap.killTweensOf(media);

        gsap.fromTo(
          media,
          {
            scale: 1.25,
          },
          {
            scale: 1,
            delay: 0.4,
            duration:
              (parseInt(this.dataset.slideSpeed || 500, 10) / 1000) + 0.4,
            ease: 'power3.out',
            overwrite: 'auto',
          }
        );
      });

      this.swiper.on('slideChangeTransitionEnd', () => {
        const inactiveSlide = this.querySelector('.swiper-slide:not(.swiper-slide-active)');
        const media = getMedia(inactiveSlide);

        if (!media) return;

        gsap.killTweensOf(media);
      });
    }

    const focusableSelector = 'a, button, input, textarea, select, details, [tabindex]';
    const fixFocus = () => {
      this._focusRaf = null;
      const keepAllSlidesTabbable = Boolean(this.closest('.resource-list__carousel'));

      this.querySelectorAll('.swiper-slide').forEach(slide => {
        const isDuplicate = slide.classList.contains('swiper-slide-duplicate');
        const isVisible = keepAllSlidesTabbable
          ? !isDuplicate
          : !isDuplicate && (slide.classList.contains('swiper-slide-visible') || slide.classList.contains('swiper-slide-active'));

        slide.querySelectorAll(focusableSelector).forEach(el => {
          if (isVisible) {
            if (el.hasAttribute('data-original-tabindex')) {
              el.setAttribute('tabindex', el.getAttribute('data-original-tabindex'));
              el.removeAttribute('data-original-tabindex');
            } else if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            if (el.getAttribute('tabindex') !== '-1') {
              if (el.hasAttribute('tabindex')) {
                el.setAttribute('data-original-tabindex', el.getAttribute('tabindex'));
              }
              el.setAttribute('tabindex', '-1');
            }
          }
        });
      });
    };

    const scheduleFocusFix = () => {
      if (this._focusRaf) return;
      this._focusRaf = requestAnimationFrame(fixFocus);
    };

    this.swiper.on('init', scheduleFocusFix);
    this.swiper.on('slideChangeTransitionEnd', scheduleFocusFix);
    this.swiper.on('resize', scheduleFocusFix);
    scheduleFocusFix();

    if (this.swiper.autoplay && this.dataset.mediaGallery === "true") {
      const swiper = this.swiper;
      const el = this;

      let isHover = false;

      // Pause autoplay when hover
      el.addEventListener("mouseenter", () => {
        if (isHover) return;
        isHover = true;

        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        }
      });

      // Resume autoplay when mouse leave
      el.addEventListener("mouseleave", () => {
        if (!isHover) return;
        isHover = false;

        // delay for smooth (optional)
        requestAnimationFrame(() => {
          if (!swiper.destroyed) {
            swiper.autoplay.start();
          }
        });
      });

      // Optional: pause when user drag (better than default)
      swiper.on("touchStart", () => {
        if (swiper.autoplay.running) {
          swiper.autoplay.stop();
        }
      });

      swiper.on("touchEnd", () => {
        if (!isHover && !swiper.destroyed) {
          swiper.autoplay.start();
        }
      });
    }

    const externalArrows = document.querySelector(
      `slider-arrows[target="${this.id}"]`
    );

    const bindArrows = (prev, next) => {
      if (!prev || !next) return;

      prev.addEventListener("click", () => this.swiper.slidePrev());
      next.addEventListener("click", () => this.swiper.slideNext());
    };

    // Update disabled state for arrows outside the slider component
    const updateArrowState = (prev, next) => {
      if (!this.swiper) return;

      const isBeginning = this.swiper.isBeginning;
      const isEnd = this.swiper.isEnd;

      if (prev) prev.classList.toggle("swiper-button-disabled", isBeginning);
      if (next) next.classList.toggle("swiper-button-disabled", isEnd);
    };

    const bindArrowEvents = (prev, next) => {
      this.swiper.on("init", () => updateArrowState(prev, next));
      this.swiper.on("slideChange", () => updateArrowState(prev, next));
      this.swiper.on("reachBeginning", () => updateArrowState(prev, next));
      this.swiper.on("reachEnd", () => updateArrowState(prev, next));
      this.swiper.on("fromEdge", () => updateArrowState(prev, next));
    };

    if (externalArrows) {
      // Bind external arrows
      const prev = externalArrows.querySelector(".swiper-button-prev");
      const next = externalArrows.querySelector(".swiper-button-next");

      bindArrows(prev, next);
      updateArrowState(prev, next);
      bindArrowEvents(prev, next);

    } else {
      // Internal arrows inside
      const prev = this.querySelector(".swiper-button-prev");
      const next = this.querySelector(".swiper-button-next");

      bindArrows(prev, next);
    }

    this.dispatchEvent(
      new CustomEvent("slider:init", {
        detail: { swiper: this.swiper },
        bubbles: true,
      })
    );
  }


}
customElements.define("slider-component", SliderComponent);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}
customElements.define("account-icon", AccountIcon);

class VideoBackgroundComponent extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('[ref="videoElement"], video');
    if (!video) return;

    const sources = this.querySelectorAll('[ref="videoSources"], source');
    let changed = false;

    for (const source of sources) {
      const candidate =
        source.getAttribute("data-video-source") ||
        (source.dataset ? source.dataset.videoSource : "") ||
        source.getAttribute("data-src") ||
        source.getAttribute("data-source");

      const nextSrc = candidate && candidate.trim();
      if (!nextSrc) continue;

      if (source.getAttribute("src") !== nextSrc) {
        source.setAttribute("src", nextSrc);
        changed = true;
      }
    }

    if (changed) {
      try {
        video.load();
      } catch (_) {}

      if (video.autoplay && video.muted) {
        const p = video.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    }
  }
}
customElements.define("video-background-component", VideoBackgroundComponent);

class ScrollAuto extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.container = this.querySelector(
      "[data-scroll-auto-container]"
    );

    if (!this.container) return;

    this.items =
      this.container.querySelectorAll(":scope > *");

    /**
     * Click -> auto-center
     */
    this.items.forEach((item) => {
      item.addEventListener("click", () => {
        this.handleItemClick(item, this.container);
      });
    });

    /**
     * Detect left/right overflow
     */
    this.updateScrollState();

    this.container.addEventListener(
      "scroll",
      this.updateScrollState.bind(this)
    );

    window.addEventListener(
      "resize",
      this.updateScrollState.bind(this)
    );
  }

  /* ===============================
    AUTO CENTER ITEM
  =============================== */
  handleItemClick(item, container) {
    const rectContainer =
      container.getBoundingClientRect();

    const rectItem =
      item.getBoundingClientRect();

    const currentScroll =
      container.scrollLeft;

    const itemCenter =
      rectItem.left + rectItem.width / 2;

    const containerCenter =
      rectContainer.left +
      rectContainer.width / 2;

    const offset =
      itemCenter - containerCenter;

    const target =
      currentScroll + offset;

    this.scrollTo(container, target);
  }

  /* ===============================
    SCROLL TO
  =============================== */
  scrollTo(container, target) {
    gsap.to(container, {
      scrollTo: { x: target },
      duration: 0.45,
      ease: theme.config.gsapEasePower2Out,

      onUpdate: () => {
        this.updateScrollState();
      },

      onComplete: () => {
        this.updateScrollState();
      }
    });
  }

  /* ===============================
    UPDATE LEFT / RIGHT STATE
  =============================== */
  updateScrollState() {
    const container = this.container;

    if (!container) return;

    const scrollLeft =
      container.scrollLeft;

    const maxScroll =
      container.scrollWidth -
      container.clientWidth;

    this.updateRemainClasses(
      scrollLeft,
      maxScroll
    );
  }

  /* ===============================
    REMAIN CLASS ON ITEMS
  =============================== */
  updateRemainClasses(scrollLeft, maxScroll) {
    const container = this.container;

    if (!container || !this.items?.length) return;

    const items = Array.from(this.items);
    const containerRect =
      container.getBoundingClientRect();
    const epsilon = 1;

    const hasLeftRemain = scrollLeft > 0;
    const hasRightRemain =
      scrollLeft < maxScroll - epsilon;

    items.forEach((item) => {
      item.classList.remove(
        "remain-left",
        "remain-right"
      );

      const rect = item.getBoundingClientRect();

      if (
        hasLeftRemain &&
        rect.left < containerRect.left - epsilon
      ) {
        item.classList.add("remain-left");
      }

      if (
        hasRightRemain &&
        rect.right > containerRect.right + epsilon
      ) {
        item.classList.add("remain-right");
      }
    });
  }
}
customElements.define("scroll-auto", ScrollAuto);

class HeroLogoContainer extends HTMLElement {
  constructor() {
    super();
    this._resize = this._resize.bind(this);
    this._debouncedResize = theme.debounce(this._resize, 50);
    this._observer = new ResizeObserver(this._debouncedResize);
    this._textHoverCleanup = null;
  }

  static SHADOW_LAYERS = 20;
  static TEXT_MOVE_FACTOR = 0.2;
  static SHADOW_MOVE_FACTOR = 4;
  static GSAP_EASE = "power4.out";

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this._type = this.dataset.type;
    this._textEffect = this.dataset.effect;
    this.animationRepeat = this.dataset.animationRepeat;
    this.toggleActions = this.animationRepeat === "true"
      ? "play none restart reset"
      : "play none none none";

    if (this._type === "text") {
      this._initTextMode();
    } else {
      this._animateImage();
    }
  }

  disconnectedCallback() {
    this._observer.disconnect();
    this._textHoverCleanup?.();
  }

  _initTextMode() {
    this._texts = this.querySelectorAll(".hero-logo-inner");
    requestAnimationFrame(this._resize);
    this._observer.observe(this);

    if (this._textEffect === "shadow" && !theme.media.isMobile) {
      this._textHoverCleanup = this._textHover(this, this.dataset.shadowColor);
    }
  }

  _textHover(el, shadowColor) {
    if (!el) return () => {};

    const shadow = { x: 0, y: 0 };

    const updateShadow = () => {
      const shadows = Array.from(
        { length: HeroLogoContainer.SHADOW_LAYERS },
        (_, i) => `${shadow.x * (i + 1)}px ${shadow.y * (i + 1)}px ${shadowColor}`
      );
      el.style.textShadow = shadows.join(", ");
    };

    const onMouseMove = (event) => {
      const rect = el.getBoundingClientRect();
      const currentX = gsap.getProperty(el, "x") || 0;
      const currentY = gsap.getProperty(el, "y") || 0;
      const centerX = rect.left - currentX + rect.width / 2;
      const centerY = rect.top - currentY + rect.height / 2;
      const deltaX = event.clientX - centerX;
      const deltaY = event.clientY - centerY;
      const ratioX = deltaX / window.innerWidth;
      const ratioY = deltaY / window.innerHeight;

      gsap.to(el, {
        x: deltaX * HeroLogoContainer.TEXT_MOVE_FACTOR,
        y: deltaY * HeroLogoContainer.TEXT_MOVE_FACTOR,
        duration: 0.4,
        ease: HeroLogoContainer.GSAP_EASE
      });

      gsap.to(shadow, {
        x: ratioX * HeroLogoContainer.SHADOW_MOVE_FACTOR,
        y: ratioY * HeroLogoContainer.SHADOW_MOVE_FACTOR,
        duration: 0.4,
        ease: HeroLogoContainer.GSAP_EASE,
        onUpdate: updateShadow
      });
    };

    const onMouseLeave = () => {
      gsap.to(el, {
        x: 0,
        y: 0,
        duration: 0.4,
        ease: "back.out(3)"
      });
      gsap.to(shadow, {
        x: 0,
        y: 0,
        duration: 0.8,
        ease: HeroLogoContainer.GSAP_EASE,
        onUpdate: updateShadow
      });
    };

    el.addEventListener("mousemove", onMouseMove);
    el.addEventListener("mouseleave", onMouseLeave);

    return () => {
      el.removeEventListener("mousemove", onMouseMove);
      el.removeEventListener("mouseleave", onMouseLeave);
    };
  }

  _animateImage() {
    if (this.dataset.effect === "none") return;
    this.classList.add("ready");

    const imageWrapper = this.querySelector(".hero-logo__image-wrapper");
    const image = imageWrapper?.querySelector(".hero-logo__image");
    if (!image) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: imageWrapper,
        start: "top bottom",
        end: "bottom top",
        toggleActions: this.toggleActions
      }
    });

    tl.from(image, {
      filter: "blur(30px)",
      scale: 1.1,
      yPercent: 10,
      duration: 1,
      delay: 0.3,
      ease: "expo.out"
    });
  }

  _resize() {
    const styles = getComputedStyle(this);
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    const containerWidth = this.clientWidth - paddingX;

    if (!containerWidth || !this._texts?.length) return;

    const maxFontSize = theme.media.isMobile ? 55 : 260;
    const minFontSize = 8;

    this._texts.forEach((text) => {
      text.style.whiteSpace = "nowrap";
      text.style.fontSize = `${maxFontSize}px`;

      let textWidth = text.getBoundingClientRect().width;
      if (!textWidth) return;

      let scale = containerWidth / textWidth;
      let finalSize = Math.min(maxFontSize, Math.max(minFontSize, maxFontSize * scale));

      text.style.fontSize = `${finalSize}px`;

      textWidth = text.getBoundingClientRect().width;

      if (textWidth > containerWidth) {
        const adjustScale = containerWidth / textWidth;
        finalSize = finalSize * adjustScale;
        text.style.fontSize = `${finalSize}px`;
      }
    });

    this.classList.add("ready");
  }
}
if (!customElements.get("hero-logo-container")) {
  customElements.define("hero-logo-container", HeroLogoContainer);
}

class CountdownBlock extends HTMLElement {
  constructor() {
    super();
    this.countdownValue = this.dataset.countdownValue;
    if (!this.countdownValue) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.initializeCountdown(this.countdownValue);
          this.observer.unobserve(this);
        }
      });
    },
      {
        rootMargin: '0px 0px 100px 0px',
      });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this._countdownInterval) clearInterval(this._countdownInterval);
    this._countdownInterval = null;
    this.observer?.disconnect?.();
  }

  initializeCountdown(countdownValue) {
    let countDownDate = null;
    if (typeof countdownValue === 'string' && /^\d+$/.test(countdownValue)) {
      const n = Number(countdownValue);
      countDownDate = n < 1e12 ? n * 1000 : n;
    } else {
      countDownDate = Date.parse(countdownValue);
    }
    if (!Number.isFinite(countDownDate)) return;

    if (typeof window.countdownStrings !== 'object' || window.countdownStrings === null) {
      return;
    }

    const countdownfunction = () => {
      const now = new Date().getTime();
      const distancePrev = countDownDate - now;
      const distanceNext = countDownDate - now - 1000;

      if (distancePrev < 1000) {
        clearInterval(this._countdownInterval);
        this._countdownInterval = null;
        this.parentElement?.querySelector('.timer-message')?.classList.add('completed');
        this.remove();
        return;
      }

      const days = Math.floor(distancePrev / (1000 * 60 * 60 * 24));

      const calculateTime = (distance) => {
        const totalHours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const totalMinutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const totalSeconds = Math.floor((distance % (1000 * 60)) / 1000);

        const format = (num) => `0${num}`.slice(-2);

        const hours = format(totalHours);
        const minutes = format(totalMinutes);
        const seconds = format(totalSeconds);

        return {
          tens: (timeStr) => Math.floor(timeStr / 10),
          units: (timeStr) => timeStr % 10,
          hours: hours,
          minutes: minutes,
          seconds: seconds,
        };
      };

      const prev = calculateTime(distancePrev);
      const next = calculateTime(distanceNext);

      const dayText = window.countdownStrings.days
      const hourText = window.countdownStrings.hours
      const minuteText = this.dataset.countdownProductBlock === 'true' ? window.countdownStrings.minutes_short : window.countdownStrings.minutes
      const secondText = this.dataset.countdownProductBlock === 'true' ? window.countdownStrings.seconds_short : window.countdownStrings.seconds

      const clockItems = [
        days > 0 ? days > 9 ?
          `<div class="clock-item relative flex-center flex-column rounded-circle days">
          <span class="block text uppercase">${dayText}</span>
          <span class="num flex justify-center overflow-hidden">${days}</span>
        </div>`
          : `<div class="clock-item relative flex-center flex-column rounded-circle days">
          <span class="block text uppercase">${dayText}</span>
          <span class="num flex justify-center overflow-hidden">
            <span class="block tens">0</span>
            <span class="block units">${days}</span>
          </span>
        </div>`
          : '',

        `<div class="clock-item relative flex-center flex-column rounded-circle">
          <span class="block text uppercase">${hourText}</span>
          <span class="num flex justify-center overflow-hidden">
            <span class="block tens">
              <span class="block next" data-value="${next.tens(prev.hours)}">${next.tens(next.hours)}</span>
              <span class="block prev" data-value="${prev.tens(prev.hours)}">${prev.tens(prev.hours)}</span>
            </span>
            <span class="block units">
              <span class="block next" data-value="${next.units(prev.hours)}">${next.units(next.hours)}</span>
              <span class="block prev" data-value="${prev.units(prev.hours)}">${prev.units(prev.hours)}</span>
            </span>
          </span>
        </div>`,

        `<div class="clock-item relative flex-center flex-column rounded-circle">
          <span class="block text uppercase">${minuteText}</span>
          <span class="num flex justify-center overflow-hidden">
            <span class="block tens">
              <span class="block next" data-value="${next.tens(next.minutes)}">${next.tens(next.minutes)}</span>
              <span class="block prev" data-value="${prev.tens(prev.minutes)}">${prev.tens(prev.minutes)}</span>
            </span>
            <span class="block units">
              <span class="block next" data-value="${next.units(next.minutes)}">${next.units(next.minutes)}</span>
              <span class="block prev" data-value="${prev.units(prev.minutes)}">${prev.units(prev.minutes)}</span>
            </span>
          </span>
        </div>`,

        `<div class="clock-item relative flex-center flex-column rounded-circle">
          <span class="block text uppercase">${secondText}</span>
          <span class="num flex justify-center overflow-hidden">
            <span class="block tens">
              <span class="block next" data-value="${next.tens(next.seconds)}">${next.tens(next.seconds)}</span>
              <span class="block prev" data-value="${prev.tens(prev.seconds)}">${prev.tens(prev.seconds)}</span>
            </span>
            <span class="block units">
              <span class="block next" data-value="${next.units(next.seconds)}">${next.units(next.seconds)}</span>
              <span class="block prev" data-value="${prev.units(prev.seconds)}">${prev.units(prev.seconds)}</span>
            </span>
          </span>
        </div>`
      ].join('');

      this.innerHTML = clockItems;

      this.querySelectorAll('.clock-item:not(.days) .num > span').forEach(element => {
        if (element.querySelector('.next').dataset.value != element.querySelector('.prev').dataset.value) {
          element.classList.add('digit-roll-in');
          element.querySelector('span.next').classList.add('digit-fade');
          element.querySelector('span.prev').classList.add('digit-fade');
        }
      });
    }

    countdownfunction();

    if (this._countdownInterval) clearInterval(this._countdownInterval);
    this._countdownInterval = setInterval(() => {
      countdownfunction();
    }, 1000);
  }
}
if (!customElements.get('countdown-block')) {
  customElements.define('countdown-block', CountdownBlock);
}

// This prevents "modal button stops working after variant change" without affecting variant state.
theme.getModalFromContext =
  theme.getModalFromContext ||
  ((button, selector) => {
    if (!selector || selector[0] !== '#') return null;

    const scopedRoot =
      button?.closest?.('quick-add-modal') ||
      button?.closest?.('product-information') ||
      button?.closest?.('modal-component');

    if (scopedRoot) {
      const scopedModal = scopedRoot.querySelector(`modal-component${selector}`);
      if (scopedModal) return scopedModal;
    }

    return document.querySelector(selector);
  });

theme.bindModalToggles =
  theme.bindModalToggles ||
  (() => {
    document.querySelectorAll('[data-modal-toggle]').forEach((button) => {
      const selector = button.getAttribute('data-modal-toggle');
      if (!selector || selector[0] !== '#') return;
      // Re-bind safely if the modal node was replaced
      if (button.__modalToggleSelector === selector && button.__modalToggleHandler) return;
      if (button.__modalToggleHandler) {
        button.removeEventListener('click', button.__modalToggleHandler);
      }

      const handler = (event) => {
        event.preventDefault();
        const modal = theme.getModalFromContext?.(button, selector) || document.querySelector(selector);
        if (!modal || typeof modal.show !== 'function' || typeof modal.close !== 'function') return;
        if (button.classList.contains('header-localization__button')) {
          modal.show(button, true);
        } else {
          modal.isOpen ? modal.close() : modal.show(button);
        }
      };

      button.__modalToggleSelector = selector;
      button.__modalToggleHandler = handler;
      button.addEventListener('click', handler);

    });
  });

// Bind once on initial load as well (ModalComponent no longer binds in its constructor)
theme.bindModalToggles?.();

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        if (this.__bound) return;
        this.__bound = true;

        this.addEventListener('change', (event) => {
          const target = this.#getInputForEventTarget(event.target);
          if (!target) return;

          theme.publish(theme.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });
      }

      #getInputForEventTarget(target) {
        if (!target) return null;
        if (target.tagName === 'SELECT') return target.selectedOptions?.[0] || null;
        return target;
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option:checked, fieldset input:checked'))
          .map(({ dataset }) => dataset.optionValueId)
          .filter(Boolean);
      }
    }
  );
}

if (!customElements.get('variant-swatches-picker')) {
  class VariantSwatchesPicker extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Optional JSON blobs (some contexts/snippets don't render all variants JSON)
      this.allVariantsEl = this.querySelector('[data-all-variants]');
      this.selectedVariantEl = this.querySelector('[data-selected-variant]');

      this.addEventListener("change", (event) => {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);
        if (this.allVariantsEl) this.updateVariant();

        theme.publish(
          theme.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          }
        );
      });
    }

    updateVariant() {
      if (!this.allVariantsEl) return;

      let allVariants;
      try {
        allVariants = JSON.parse(this.allVariantsEl.textContent);
      } catch (e) {
        console.warn('VariantSwatchesPicker: invalid [data-all-variants] JSON', e);
        return;
      }
      const selectedOptions = Array.from(this.querySelectorAll('select, input:checked')).map(el => el.value);

      const currentVariant = allVariants.find(variant => {
        return variant.options.every((option, index) => option === selectedOptions[index]);
      });

      if (currentVariant) {
        if (this.selectedVariantEl) this.selectedVariantEl.textContent = JSON.stringify(currentVariant);
        this.updateProductDetails(currentVariant);
      }
    }

    updateProductDetails(variant) {
      const card = this.closest('product-card');
      if (!card) return;

      // Update Price
      const priceWrapper = card.querySelector('.price');
      if (priceWrapper && variant.price) {
        priceWrapper.innerHTML = Shopify.formatMoney(variant.price);
      }

      // Update Image
      if (variant.featured_image) {
        const img = card.querySelector('.product-media [data-main-image]');
        if (img) {
          img.srcset = variant.featured_image.src;
        }
      }
    }

    updateSelectionMetadata({ target }) {
      const { value, tagName } = target;

      if (tagName === "SELECT" && target.selectedOptions.length) {
        const selectedOption = target.selectedOptions[0];
        Array.from(target.options).forEach(opt => opt.removeAttribute("selected"));
        selectedOption.setAttribute("selected", "selected");

        const swatchValue = selectedOption.dataset.optionSwatchValue;
        const selectedDropdownSwatchValue = target
          .closest(".variant-option__buttons")
          .querySelector("[data-selected-value] > .swatch");

        if (selectedDropdownSwatchValue) {
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
            selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
          } else {
            selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
            selectedDropdownSwatchValue.classList.add("swatch--unavailable");
          }

          selectedDropdownSwatchValue.style.setProperty(
            "--swatch-focal-point",
            selectedOption.dataset.optionSwatchFocalPoint || "unset"
          );
        }

        const textValue = target.closest(".select").querySelector('.dropdown-value-text');
        if (textValue) textValue.textContent = value;

      } else if (tagName === "INPUT" && target.type === "radio") {
        const selectedSwatchValue = target
          .closest(`.variant-option__buttons`)
          .querySelector("[data-selected-value]");
        if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        this.updateVariantImages(target);
        this.updateVariantLink(target);
      }
    }

    updateVariantImages(target) {
      const option = target.closest(".variant-option__swatch");
      const variantImage = option.querySelector(".swatch").dataset.swatchImageUrl;

      if (variantImage) {
        const product =
          this.closest(".product-card, .resource-product-card ").querySelector(".product-media, .resource-product-card__media");
        const productImage = product.querySelector("[data-main-image]");
        if (productImage) {
          productImage.setAttribute("srcset", variantImage);
        }
      }
    }

    updateVariantLink(target) {
      const variantLink = target.dataset.variantId.replace(/"/g, '').trim();
      const productTitleLink = this.closest(".product-card, .resource-product-card").querySelector(".product-title-link");

      const productImageLink = this.closest(".product-card, .resource-product-card").querySelector(".card-img-link");

      const updateUrlVariant = (link) => {
        const url = new URL(link.href);

        url.searchParams.set('variant', variantLink);

        link.href = url.toString();
      };

      updateUrlVariant(productTitleLink);
      updateUrlVariant(productImageLink);
    }

    getInputForEventTarget(target) {
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }

    get selectedOptionValues() {
      return Array.from(
        this.querySelectorAll("select option[selected], fieldset input:checked")
      ).map(({ dataset }) => dataset.optionValueId);
    }
  }
  customElements.define("variant-swatches-picker", VariantSwatchesPicker);
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.stickySubmitButton = document.querySelector('[ref="addToCartButton"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        if (this.stickySubmitButton) {
          this.stickySubmitButton.setAttribute('aria-disabled', true);
          this.stickySubmitButton.classList.add('loading');
          this.stickySubmitButton.querySelector('.loading__spinner').classList.remove('hidden');
        }

        const config = theme.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (!formData.get('quantity')) {
          const container =
            this.closest('product-information') ||
            this.closest('[data-product-id]');
          const qtyInput = container?.querySelector('.quantity__input[name="quantity"]');
          const qty = qtyInput ? parseInt(qtyInput.value) : 1;
          formData.append('quantity', Number.isFinite(qty) && qty > 0 ? String(qty) : '1');
        }

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const addUrl = (() => {
          const base = `${routes.cart_add_url || '/cart/add'}`;
          if (base.includes('.js')) return base;
          const [path, qs] = base.split('?');
          return `${path}.js${qs ? `?${qs}` : ''}`;
        })();

        fetch(addUrl, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              theme.publish(theme.PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              theme.publish(theme.PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickModal = this.closest('quick-view-modal') || this.closest('quick-add-modal');
            if (quickModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (this.stickySubmitButton) {
              this.stickySubmitButton.classList.remove('loading');
              if (!this.error) this.stickySubmitButton.removeAttribute('aria-disabled');
              this.stickySubmitButton.querySelector('.loading__spinner').classList.add('hidden');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          viewer: null,
        };
        this._initialMediaId = null;
        this._animatedToInitial = false;
        this._onWindowLoad = null;
        this._onSliderInit = this._onSliderInit.bind(this);
        this._onSlideChange = this._onSlideChange.bind(this);
      }

      connectedCallback() {
        this.elements.viewer = this._getViewer();
        if (!this.elements.viewer && this._isCarousel()) return;

        this._initialMediaId = this.dataset.initialMediaId || null;

        this._thumbs = this.querySelector(`slider-thumbnail[target="${this.elements.viewer?.id || ''}"]`) || null;
        this._bindThumbnailClicks();

        if (this.elements.viewer) this.addEventListener('slider:init', this._onSliderInit);

        if (this.elements.viewer && this.elements.viewer.swiper) {
          this._bindSwiper(this.elements.viewer.swiper);
        }
        this._bindMediaLightboxButtons();
      }

      disconnectedCallback() {
        this.removeEventListener('slider:init', this._onSliderInit);
        if (this._onWindowLoad) window.removeEventListener('load', this._onWindowLoad);
        this._onWindowLoad = null;
        this._unbindThumbnailClicks();
        this._unbindSwiper();
        this._unbindMediaLightboxButtons();
      }

      _isCarousel() {
        return (this.dataset.layoutType || '').toLowerCase() === 'carousel';
      }

      _isGrid() {
        return (this.dataset.layoutType || '').toLowerCase() === 'grid';
      }

      _getViewer() {
        return this.querySelector('slider-component');
      }

      _onSliderInit(e) {
        // Ensure the init event is for the viewer inside this gallery
        if (!this.contains(e.target)) return;
        if (e.target !== this.elements.viewer) return;
        const swiper = e.detail?.swiper;
        if (swiper) {
          this._bindSwiper(swiper);
        }
      }

      _bindSwiper(swiper) {
        if (!swiper || this._swiper === swiper) return;
        this._unbindSwiper();
        this._swiper = swiper;
        this._swiper.on('slideChange', this._onSlideChange);
        // In grid+carousel_on_mobile, keep carousel "is-active" synced (not the grid DOM).
        if (this._isGrid()) {
          const activeEl = this.elements.viewer?.querySelector('.swiper-slide.swiper-slide-active[data-media-id]');
          const mediaId = activeEl?.dataset?.mediaId;
          if (mediaId) this._syncCarouselActiveClass(mediaId);
        } else {
          this._syncActiveClass();
        }

        // Carousel requirement: on refresh, animate to the active variant media AFTER page load.
        if (this._isCarousel()) this._animateToInitialAfterLoad();
      }

      _unbindSwiper() {
        if (!this._swiper) return;
        try {
          this._swiper.off('slideChange', this._onSlideChange);
        } catch {
          // ignore
        }
        this._swiper = null;
      }

      _onSlideChange() {
        if (this._isGrid()) {
          const activeEl = this.elements.viewer?.querySelector('.swiper-slide.swiper-slide-active[data-media-id]');
          const mediaId = activeEl?.dataset?.mediaId;
          if (mediaId) {
            this._syncCarouselActiveClass(mediaId);
            this._syncThumbActive(mediaId);
          }
        } else {
          this._syncActiveClass();
          this._syncThumbActive();
        }
        this.preventStickyHeader();
      }

      _syncActiveClass() {
        const slides = this._getSlides();
        slides.forEach((el) => el.classList.remove('is-active'));
        const active = this._getActiveSlide();
        active?.classList?.add('is-active');
      }

      _normalizeMediaId(id) {
        if (!id) return '';
        const parts = String(id).split('-');
        return parts[parts.length - 1];
      }

      _matchesMediaId(candidate, target) {
        if (!candidate || !target) return false;
        if (candidate === target) return true;
        const cNorm = this._normalizeMediaId(candidate);
        const tNorm = this._normalizeMediaId(target);
        return cNorm !== '' && cNorm === tNorm;
      }

      _syncCarouselActiveClass(mediaId) {
        if (!this.elements.viewer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide[data-media-id]'));
        slides.forEach((el) => el.classList.remove('is-active'));
        const active = slides.find((el) => this._matchesMediaId(el.dataset.mediaId, mediaId));
        active?.classList?.add('is-active');
      }

      _getSlides() {
        if (this._isGrid()) {
          const container = this._getGridContainer();
          if (!container) return [];
          return Array.from(container.querySelectorAll('[data-media-id]'));
        }

        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('.swiper-slide[data-media-id]'));
      }

      _getActiveSlide() {
        if (this._isGrid()) {
          const slides = this._getSlides();
          return slides.find((s) => s.classList.contains('is-active')) || slides[0] || null;
        }

        // Prefer Swiper active element if available
        const activeEl = this.elements.viewer?.querySelector('.swiper-slide.swiper-slide-active[data-media-id]');
        if (activeEl) return activeEl;
        const slides = this._getSlides();
        return slides.find((s) => s.classList.contains('is-active')) || slides[0] || null;
      }

      _getGridContainer() {
        return this.querySelector('.resource-list');
      }

      _setCarouselActiveMedia(mediaId, prepend = false, speed = undefined) {
        if (!mediaId) return;
        if (!this.elements.viewer) return;

        if (!this._swiper) {
          requestAnimationFrame(() => this._setCarouselActiveMedia(mediaId, prepend, speed));
          return;
        }

        const all = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide[data-media-id]'));
        const realSlides = all.filter((el) => !el.classList.contains('swiper-slide-duplicate'));
        const realIndex = realSlides.findIndex((el) => this._matchesMediaId(el.dataset.mediaId, mediaId));
        if (realIndex < 0) return;

        // Slide to the matching media
        if (this._swiper.params?.loop && typeof this._swiper.slideToLoop === 'function') {
          this._swiper.slideToLoop(realIndex, speed);
        } else {
          this._swiper.slideTo(realIndex, speed);
        }

        // Optional: if requested, move the slide DOM to the front (rarely needed; parity with Dawn)
        if (prepend) {
          const el = realSlides[realIndex];
          const wrapper = el?.parentElement;
          if (wrapper && wrapper.firstChild !== el) wrapper.prepend(el);
          this._swiper.update?.();
        }

        this._syncCarouselActiveClass(mediaId);
        this._syncThumbActive(mediaId);
      }

      setActiveMedia(mediaId, prepend = false, speed = undefined) {
        if (!mediaId) return;

        if (this._isGrid()) {
          const container = this._getGridContainer();
          if (!container) return;

          const all = Array.from(container.querySelectorAll('[data-media-id]'));
          const active = all.find((el) => this._matchesMediaId(el.dataset.mediaId, mediaId)) || all[0];
          if (!active) return;

          all.forEach((el) => el.classList.remove('is-active'));
          active.classList.add('is-active');

          if (container.firstChild !== active) container.prepend(active);

          this._syncThumbActive(mediaId);

          if (this.elements.viewer && window.matchMedia('(max-width: 1024px)').matches) {
            this._setCarouselActiveMedia(mediaId, false, speed);
          }
          return;
        }

        this._setCarouselActiveMedia(mediaId, prepend, speed);
      }

      _bindThumbnailClicks() {
        if (!this._thumbs) return;
        if (this._thumbs.__pmgBound) return;
        this._thumbs.__pmgBound = true;

        this._thumbPointer = { active: false, moved: false, x: 0, y: 0, t: 0 };
        this._thumbUserInteracting = false;

        const start = (e) => {
          const pt = e.touches?.[0] || e.changedTouches?.[0] || e;
          if (!pt) return;
          this._thumbPointer.active = true;
          this._thumbPointer.moved = false;
          this._thumbPointer.x = pt.clientX;
          this._thumbPointer.y = pt.clientY;
          this._thumbPointer.t = Date.now();
          this._thumbUserInteracting = true;
        };

        const move = (e) => {
          if (!this._thumbPointer.active) return;
          const pt = e.touches?.[0] || e.changedTouches?.[0] || e;
          if (!pt) return;
          const dx = Math.abs(pt.clientX - this._thumbPointer.x);
          const dy = Math.abs(pt.clientY - this._thumbPointer.y);
          if (dx > 6 || dy > 6) this._thumbPointer.moved = true;
        };

        const end = () => {
          this._thumbPointer.active = false;
          setTimeout(() => {
            this._thumbUserInteracting = false;
          }, 180);
        };

        this._onThumbClick = (e) => {
          if (this._thumbPointer?.moved) return;

          const el = e.target?.closest?.('[data-media-id]');
          if (!el || !this._thumbs.contains(el)) return;
          let mediaId = el.dataset.mediaId;
          if (!mediaId) return;
          if (el.classList?.contains?.('swiper-slide-duplicate')) {
            const items = Array.from(this._thumbs.querySelectorAll('[data-media-id]'));
            const primary = items.find(
              (i) => !i.classList.contains('swiper-slide-duplicate') && this._matchesMediaId(i.dataset.mediaId, mediaId)
            );
            if (primary?.dataset?.mediaId) mediaId = primary.dataset.mediaId;
          }
          this.setActiveMedia(mediaId, false);
        };

        this._onThumbKeydown = (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const el = e.target?.closest?.('[data-media-id]');
          if (!el || !this._thumbs.contains(el)) return;
          e.preventDefault();
          const mediaId = el.dataset.mediaId;
          if (!mediaId) return;
          this.setActiveMedia(mediaId, false);
        };

        this._thumbs.addEventListener('click', this._onThumbClick);
        this._thumbs.addEventListener('keydown', this._onThumbKeydown);

        // Pointer/touch tracking for reliable mobile behavior
        this._thumbs.addEventListener('touchstart', start, { passive: true });
        this._thumbs.addEventListener('touchmove', move, { passive: true });
        this._thumbs.addEventListener('touchend', end, { passive: true });
        this._thumbs.addEventListener('touchcancel', end, { passive: true });
        this._thumbs.addEventListener('pointerdown', start, { passive: true });
        this._thumbs.addEventListener('pointermove', move, { passive: true });
        this._thumbs.addEventListener('pointerup', end, { passive: true });
        this._thumbs.addEventListener('pointercancel', end, { passive: true });

        this._onThumbPointerStart = start;
        this._onThumbPointerMove = move;
        this._onThumbPointerEnd = end;
      }

      _unbindThumbnailClicks() {
        if (!this._thumbs) return;
        if (this._onThumbClick) this._thumbs.removeEventListener('click', this._onThumbClick);
        if (this._onThumbKeydown) this._thumbs.removeEventListener('keydown', this._onThumbKeydown);
        if (this._onThumbPointerStart) {
          this._thumbs.removeEventListener('touchstart', this._onThumbPointerStart);
          this._thumbs.removeEventListener('pointerdown', this._onThumbPointerStart);
        }
        if (this._onThumbPointerMove) {
          this._thumbs.removeEventListener('touchmove', this._onThumbPointerMove);
          this._thumbs.removeEventListener('pointermove', this._onThumbPointerMove);
        }
        if (this._onThumbPointerEnd) {
          this._thumbs.removeEventListener('touchend', this._onThumbPointerEnd);
          this._thumbs.removeEventListener('touchcancel', this._onThumbPointerEnd);
          this._thumbs.removeEventListener('pointerup', this._onThumbPointerEnd);
          this._thumbs.removeEventListener('pointercancel', this._onThumbPointerEnd);
        }
        this._onThumbClick = null;
        this._onThumbKeydown = null;
        this._onThumbPointerStart = null;
        this._onThumbPointerMove = null;
        this._onThumbPointerEnd = null;
      }

      _syncThumbActive(explicitMediaId = null) {
        if (!this._thumbs) return;
        const mediaId = explicitMediaId || this._getActiveSlide()?.dataset?.mediaId;
        if (!mediaId) return;

        const items = Array.from(this._thumbs.querySelectorAll('[data-media-id]'));
        items.forEach((el) => {
          const isActive = this._matchesMediaId(el.dataset.mediaId, mediaId);
          el.classList.toggle('is-active', isActive);
          el.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        // Keep the active thumbnail in view if a swiper is attached
        const activeEl = items.find((el) => this._matchesMediaId(el.dataset.mediaId, mediaId));
        const swiper = this._thumbs?.swiper;
        if (!swiper || !activeEl) return;

        const index = items.indexOf(activeEl);
        if (index < 0) return;

        const trackRect = this._thumbs.getBoundingClientRect();
        const elRect = activeEl.getBoundingClientRect();
        const isOutOfView =
          elRect.right > trackRect.right + 1 ||
          elRect.left < trackRect.left - 1;

        if (!isOutOfView) return;

        swiper.slideTo(index);
      }

      _animateToInitialAfterLoad() {
        if (this._animatedToInitial) return;
        const id = this._initialMediaId;
        if (!id) return;

        const all = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide[data-media-id]'));
        const realSlides = all.filter((el) => !el.classList.contains('swiper-slide-duplicate'));
        const realIndex = realSlides.findIndex((el) => el.dataset.mediaId === id);
        if (realIndex <= 0) return;

        const run = () => {
          if (this._animatedToInitial) return;
          this._animatedToInitial = true;

          setTimeout(() => {
            const speed = parseInt(this.elements.viewer?.dataset?.slideSpeed || 700);
            this.setActiveMedia(id, false, Number.isFinite(speed) ? speed : 700);
          }, 120);
        };

        if (document.readyState === 'complete') {
          run();
        } else {
          this._onWindowLoad = () => {
            run();
            window.removeEventListener('load', this._onWindowLoad);
            this._onWindowLoad = null;
          };
          window.addEventListener('load', this._onWindowLoad);
        }
      }

      preventStickyHeader() {
        const stickyHeader = document.querySelector('sticky-header');
        if (!stickyHeader) return;
        stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      // -----------------------------
      // PhotoSwipe integration
      // -----------------------------

      _bindMediaLightboxButtons() {
        if (this._onLightboxClickBound) return;

        let locked = false;

        this._onLightboxClickBound = (event) => {
          const button = event.target?.closest?.('.media-lightbox-button');
          if (!button || !this.contains(button)) return;
          if (locked) return;

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          if (button.tagName === 'BUTTON') {
            button.type = 'button';
          }

          locked = true;

          const slide = button.closest('[data-media-id]');
          const mediaId = button.dataset.mediaId || slide?.dataset?.mediaId;

          this._openLightboxForMedia(mediaId);

          requestAnimationFrame(() => {
            locked = false;
          });
        };

        this.addEventListener('click', this._onLightboxClickBound, true);
      }

      _unbindMediaLightboxButtons() {
        if (!this._onLightboxClickBound) return;
        this.removeEventListener('click', this._onLightboxClickBound);
        this._onLightboxClickBound = null;
      }

      _buildLightboxDataSource() {
        const items = [];
        const slides = this._getSlides();

        slides.forEach((slide) => {
          const mediaId = slide.dataset.mediaId;
          if (!mediaId) return;

          const productModel = slide.querySelector('product-model');
          if (productModel) {
            const template = productModel.querySelector('template');
            if (template) {
              const content = template.content.querySelector('model-viewer');
              if (content) {
                items.push({
                  type: 'model',
                  mediaId,
                  domElement: content,
                  width: 1200,
                  height: 1200,
                });
                return;
              }
            }
            const modelElement = slide.querySelector('model-viewer');
            if (modelElement) {
              items.push({
                type: 'model',
                mediaId,
                domElement: modelElement,
                width: modelElement.offsetWidth || 1200,
                height: modelElement.offsetHeight || 1200,
              });
              return;
            }
          }

          const videoElement = slide.querySelector('video, iframe, .media-video, deferred-media');
          if (videoElement) {
            const type = videoElement.tagName === 'IFRAME' || videoElement.querySelector('iframe') ? 'external_video' : 'video';
            items.push({
              type,
              mediaId,
              domElement: videoElement,
              width: videoElement.offsetWidth || 1200,
              height: videoElement.offsetHeight || 1200,
            });
            return;
          }

          const img = slide.querySelector('img');
          if (!img) return;

          const src = img.currentSrc || img.src;
          if (!src) return;

          const width = img.naturalWidth || img.width || 1200;
          const height = img.naturalHeight || img.height || 1200;

          items.push({
            src,
            width,
            height,
            mediaId,
            element: slide,
            thumbnailElement: img,
          });
        });

        return items;
      }

      _findLightboxIndexByMediaId(dataSource, mediaId) {
        if (!Array.isArray(dataSource) || !dataSource.length) return 0;
        if (!mediaId) return 0;
        const idx = dataSource.findIndex((item) =>
          this._matchesMediaId(item.mediaId, mediaId)
        );
        return idx >= 0 ? idx : 0;
      }

      _createLightbox(dataSource, startIndex = 0) {
        if (typeof PhotoSwipeLightbox === 'undefined') return null;

        const lightbox = new PhotoSwipeLightbox({
          dataSource,
          pswpModule: () => import(window.PhotoSwipeModule || ''),
          bgOpacity: 1,
          arrowPrevSVG: '<svg class="pswp__icn icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          arrowNextSVG: '<svg class="pswp__icn icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          closeSVG: '<svg class="pswp__icn icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 17 18" fill="none"><path opacity="0.7" d="M16.6517 0.347742C16.1835 -0.117597 15.4271 -0.116342 14.9604 0.350548L8.49418 6.82017L2.04527 0.389329C1.57826 -0.0774051 0.819343 -0.0785411 0.351815 0.387677C-0.116042 0.854223 -0.117183 1.61263 0.350158 2.07969L6.80281 8.51426L0.348285 14.9713C-0.118098 15.4394 -0.115606 16.1978 0.3531 16.6636C0.821167 17.1288 1.57802 17.1282 2.04454 16.6614L8.49792 10.2046L14.9487 16.6355C15.4157 17.1022 16.1746 17.1033 16.6422 16.6371C17.11 16.1706 17.1112 15.4122 16.6438 14.9451L10.1912 8.51053L16.6546 2.04378C17.1231 1.57504 17.1218 0.814928 16.6517 0.347742Z" fill="currentColor"/></svg>',

          allowPanToNext: false,
          allowMouseDrag: true,
          wheelToZoom: false,
          returnFocus: true,
          zoom: false,
        });

        lightbox.addFilter('thumbEl', (_thumbEl, data) => {
          return data.thumbnailElement || _thumbEl;
        });

        lightbox.on('contentLoad', (event) => {
          const { content } = event;
          if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
            event.preventDefault();
            content.element = document.createElement('div');
            content.element.className = 'pswp__video-container';
            content.element.style.pointerEvents = 'auto';
            if (content.type === 'model') content.element.addEventListener('pointerdown', (e) => e.stopPropagation());
            if (content.data.domElement) {
              content.element.appendChild(content.data.domElement.cloneNode(true));
            }
          }
        });

        lightbox.on('change', () => {
          const curr = lightbox.pswp?.currSlide?.data;
          const mediaId = curr?.mediaId;
          const activeMedia = curr?.element;

          if (mediaId && this.setActiveMedia) {
            this.setActiveMedia(mediaId, false);
          }

          if (this.querySelector('[data-shopify-xr]')) {
            const viewInSpace = this.querySelector('[data-shopify-xr]');
            if (!viewInSpace.hasAttribute('data-shopify-model3d-default-id')) {
              viewInSpace.setAttribute('data-shopify-model3d-default-id', viewInSpace.getAttribute('data-shopify-model3d-id'));
            }

            if (curr.type === 'model') {
              viewInSpace.setAttribute('data-shopify-model3d-id', mediaId);
            } else {
              viewInSpace.setAttribute('data-shopify-model3d-id', viewInSpace.getAttribute('data-shopify-model3d-default-id'));
            }
          }

          if (curr.type === 'model') {
            window.ShopifyXR?.setupXRElements();
          }
        });

        lightbox.on('close', () => {
          const index = lightbox.pswp.currIndex;
          const slides = this._getSlides();
          const slide = slides[index];
          if (slide) {
            const btn = slide.querySelector('.media-lightbox-button');
            if (btn) {
              lightbox.pswp._lastActiveElement = btn;
            }
          }
          lightbox.destroy();
        });

        lightbox.init();
        lightbox.loadAndOpen(startIndex);

        return lightbox;
      }

      _openLightboxForMedia(mediaId) {
        const dataSource = this._buildLightboxDataSource();
        if (!dataSource.length) return;

        const index = this._findLightboxIndexByMediaId(dataSource, mediaId);
        this._createLightbox(dataSource, index);
      }
    }
  );
}

if (!customElements.get('product-information')) {
  customElements.define(
    'product-information',
    class ProductInformation extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      postProcessHtmlCallbacks = [];
      lastKnownQuantity = null;
      lastKnownMediaId = null;

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
        this.setRecentlyViewed();

        this.__initialized = false;
        this.__bound = false;
      }

      connectedCallback() {
        if (this.__initialized) return;
        this.__initialized = true;

        this.initializeProductSwapUtility();

        if (!this.__bound) {
          this.__bound = true;
          this.onVariantChangeUnsubscriber = theme.subscribe(
            theme.PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-information:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.__bound = false;
        this.__initialized = false;
      }

      initializeProductSwapUtility() {
        if (this.__swapUtilityInitialized) return;
        this.__swapUtilityInitialized = true;

        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init?.();
          window?.ProductModel?.loadShopifyXR?.();
        });
      }

      initQuantityHandlers() {
        this.quantityInput = this.querySelector('.quantity__input');
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber?.();
          this.cartUpdateUnsubscriber = theme.subscribe(theme.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        this.lastKnownQuantity = this.getDesiredQuantity();
        this.lastKnownMediaId = target?.dataset?.optionMediaId ? parseInt(target.dataset.optionMediaId) : null;

        const productUrl =
          target?.dataset?.connectedProductUrl ||
          target?.dataset?.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target?.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const tag = this.tagName.toLowerCase();
          const nextNode = html.querySelector(tag);
          const variant = this.getSelectedVariant(nextNode || html);

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            const nextMain = html.querySelector('main');
            const curMain = document.querySelector('main');
            if (nextMain && curMain) {
              document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

              const nextPI = nextMain.querySelector(tag);
              if (nextPI) this.applyQuantityToNode(nextPI, this.lastKnownQuantity);

              HTMLUpdateUtility.viewTransition(
                curMain,
                nextMain,
                this.postProcessHtmlCallbacks
              );
            }
          } else {
            this.dataset.url = productUrl;
            this.updateExternalMedia(html);
            this.updateDetails(html, { fullSwap: true });
            this.afterPartialUpdate(variant);
          }
        };
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const tag = this.tagName.toLowerCase();
          const nextNode = html.querySelector(tag);
          const variant = this.getSelectedVariant(nextNode || html);

          this.updateURL(productUrl, variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateDetails(html, { fullSwap: false });
          this.afterPartialUpdate(variant);

          theme.publish(theme.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const root = productInfoNode || this;

        const byDataSelectedVariant =
          root.querySelector('[data-selected-variant]') ||
          root.querySelector('variant-selects [data-selected-variant]') ||
          root.querySelector('variant-radios [data-selected-variant]');

        const byVariantPickerJson =
          root.querySelector('variant-picker script[type="application/json"]') ||
          root.querySelector('variant-swatches-picker script[type="application/json"]');

        const jsonEl = byDataSelectedVariant || byVariantPickerJson;
        const raw = jsonEl?.textContent || jsonEl?.innerHTML;
        if (!raw) return null;

        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues?.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.querySelectorAll('form.payment-terms input[name="id"]').forEach((input) => {
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const fullUrl = `${url}${variantId ? `?variant=${variantId}` : ''}`;

        // Button share
        this.querySelector('share-button')?.updateUrl?.(
          `${window.shopUrl}${fullUrl}`
        );

        // Button "View details"
        const contextRoot = this.closest('modal-component') || this;
        contextRoot
          .querySelectorAll('.product__view-details')
          .forEach((link) => {
            link.setAttribute('href', fullUrl);
          });

        // Update URL browser data-update-url = true
        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState({}, '', fullUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings?.unavailable);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        const desired = this.getDesiredQuantity();
        const next = this.normalizeQuantity(desired, { min, max, step: data.step });
        this.quantityInput.value = String(next);

        theme.publish(theme.PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        this.setQuantityBoundries();
      }

      getDesiredQuantity() {
        const raw = this.quantityInput?.value;
        const n = raw ? parseInt(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      normalizeQuantity(value, { min, max, step }) {
        let v = Number.isFinite(value) && value > 0 ? value : min;
        if (v < min) v = min;
        if (max !== null && Number.isFinite(max) && v > max) v = max;

        const s = Number.isFinite(step) && step > 0 ? step : 1;
        if (s > 1) {
          v = min + Math.floor((v - min) / s) * s;
          if (v < min) v = min;
          if (max !== null && Number.isFinite(max) && v > max) v = max;
        }
        return v;
      }

      applyQuantityToNode(node, desiredQuantity) {
        if (!node) return;
        const input = node.querySelector('.quantity__input[name="quantity"]');
        if (!input) return;

        const cartQty = input.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0;
        const minRaw = input.dataset.min ? parseInt(input.dataset.min) : (input.min ? parseInt(input.min) : 1);
        const maxRule = input.dataset.max ? parseInt(input.dataset.max) : null;
        const step = input.getAttribute('step') ? parseInt(input.getAttribute('step')) : 1;

        let min = Number.isFinite(minRaw) ? minRaw : 1;
        const max = maxRule === null ? null : (Number.isFinite(maxRule) ? (maxRule - cartQty) : null);
        if (max !== null) min = Math.min(min, max);
        if (cartQty >= min) min = Math.min(min, step);

        const desired = Number.isFinite(desiredQuantity) ? desiredQuantity : this.getDesiredQuantity();
        const next = this.normalizeQuantity(desired, { min, max, step });
        input.value = String(next);
        input.setAttribute('value', String(next));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateDetails(html, { fullSwap = false } = {}) {
        const currentId = `ProductDetails-${this.dataset.section || this.sectionId}`;
        const updatedId = `ProductDetails-${this.sectionId}`;

        const current =
          this.querySelector(`#${currentId}`) || document.getElementById(currentId);
        const updated = html.getElementById(updatedId);
        if (!current || !updated) return;

        if (fullSwap) {
          this.applyQuantityToNode(updated, this.lastKnownQuantity);

          HTMLUpdateUtility.viewTransition(
            current,
            updated,
            this.postProcessHtmlCallbacks
          );
          return;
        }

        const didUpdate = this.updateLoadUpdateBlocks(current, updated);
        if (!didUpdate) {
          this.applyQuantityToNode(updated, this.lastKnownQuantity);
          HTMLUpdateUtility.viewTransition(
            current,
            updated,
            this.postProcessHtmlCallbacks
          );
        }
      }

      updateLoadUpdateBlocks(currentDetails, updatedDetails) {
        if (!currentDetails || !updatedDetails) return false;

        const all = Array.from(currentDetails.querySelectorAll('[data-load-update]'));
        if (!all.length) return false;

        const replaceImmediate = (oldNode, newNode) => {
          if (!oldNode || !newNode) return;
          const wrapper = document.createElement('div');
          HTMLUpdateUtility.setInnerHTML(wrapper, newNode.outerHTML);
          const fresh = wrapper.firstChild;
          if (!fresh) return;

          oldNode.replaceWith(fresh);
          this.postProcessHtmlCallbacks?.forEach?.((cb) => cb(fresh));
        };

        const hasAncestorMarker = (el) => {
          let p = el.parentElement;
          while (p) {
            if (p.hasAttribute?.('data-load-update')) return true;
            p = p.parentElement;
          }
          return false;
        };

        const blocks = all.filter((el) => !hasAncestorMarker(el));
        let updatedCount = 0;

        const updatedAll = Array.from(updatedDetails.querySelectorAll('[data-load-update]'));
        const updatedTop = updatedAll.filter((el) => !hasAncestorMarker(el));
        const updatedByKey = new Map();
        updatedTop.forEach((el) => {
          const key = (el.getAttribute('data-load-update') || '').trim();
          if (!key) return;
          updatedByKey.set(key, el);
        });

        const byKey = new Map();
        blocks.forEach((el) => {
          const key = (el.getAttribute('data-load-update') || '').trim();
          if (!key) return;
          const list = byKey.get(key) || [];
          list.push(el);
          byKey.set(key, list);
        });

        Array.from(byKey.entries()).forEach(([key, els]) => {
          const rendered = els.filter((n) => n?.isConnected && n.getClientRects && n.getClientRects().length > 0);
          const notDisplayNone = els.filter((n) => n?.style?.display !== 'none');
          const primary = (rendered[0] || notDisplayNone[0] || els[0]) || null;

          els.forEach((n) => {
            if (n && n !== primary) n.remove();
          });

          const updatedEl = updatedByKey.get(key) || null;
          if (!updatedEl) {
            primary?.remove();
            updatedCount += 1;
            return;
          }

          if (key === 'quantity') {
            this.applyQuantityToNode(updatedEl, this.lastKnownQuantity);
          }

          replaceImmediate(primary, updatedEl);
          updatedCount += 1;
        });

        return updatedCount > 0;
      }

      updateExternalMedia(html) {
        const current =
          this.querySelector('product-media-gallery') ||
          this.querySelector('[id^="ProductMediaGallery-"]');
        const updated =
          html.querySelector('product-media-gallery') ||
          html.querySelector('[id^="ProductMediaGallery-"]');
        if (!current || !updated) return;

        HTMLUpdateUtility.viewTransition(
          current,
          updated,
          this.postProcessHtmlCallbacks
        );
      }

      afterPartialUpdate(variant) {
        requestAnimationFrame(() => {
          this.querySelector('pickup-availability')?.update?.(variant);

          this.initQuantityHandlers();
          this.updateVariantInputs(variant?.id);
          this.updateMediaForVariant(variant);
          this.updateSku(variant);
          this.updateBarcode(variant);
          theme.bindModalToggles?.();
        });
      }

      updateSku(variant) {
        const sku = variant?.sku ?? '';
        this.querySelectorAll('[data-product-sku]').forEach((el) => {
          el.textContent = sku;
          el.toggleAttribute('data-sku-empty', !sku);
        });
      }

      updateBarcode(variant) {
        const barcode = variant?.barcode ?? '';
        this.querySelectorAll('.product__barcode').forEach((wrap) => {
          const valueEl = wrap.querySelector('[data-product-barcode-value]') || wrap.querySelector('[data-product-barcode] [data-product-barcode-value]');
          if (valueEl) valueEl.textContent = barcode;
          wrap.classList.toggle('none', !barcode);
        });
      }

      updateMediaForVariant(variant) {
        const featuredId =
          variant?.featured_media?.id ||
          variant?.featured_image?.id ||
          this.lastKnownMediaId;
        if (!featuredId) return;

        const gallery =
          this.querySelector('product-media-gallery') ||
          this.querySelector('[id^="ProductMediaGallery-"]');
        if (!gallery || typeof gallery.setActiveMedia !== 'function') return;

        gallery.setActiveMedia(`${this.sectionId}-${featuredId}`, false);
      }

      setRecentlyViewed() {
        const name = '_recently_viewed';
        const productId = parseInt(this.dataset.productId);
        let listItems = JSON.parse(localStorage.getItem(name) || '[]');
        if (!productId) return;
        if (listItems.includes(productId)) listItems = listItems.filter((id) => id !== productId);
        listItems.unshift(productId);
        localStorage.setItem(name, JSON.stringify(listItems.slice(0, 25)));
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    if (this.__initialized) return;
    this.__initialized = true;
    this.#init();
  }

  #init() {
    if (!this.dataset.url) return;
    theme.initSectionVisible({
      element: this,
      callback: () => this.#load(),
      threshold: 400
    });
  }

  async #load() {
    try {
      const response = await fetch(this.#getUrl());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('recently-viewed-products');
      if (section) this.innerHTML = section.innerHTML;
      const iconWrapper = this.querySelectorAll('icon-wrapper');
      if (iconWrapper) iconWrapper.forEach(iconWrapper => iconWrapper._init());

    } catch (err) {
      console.error('RecentlyViewedProducts load error:', err);
    }
  }

  #getUrl() {
    const listItems = JSON.parse(localStorage.getItem('_recently_viewed') || '[]');
    const currentId = parseInt(this.dataset.productId);

    if (currentId && listItems.includes(currentId)) {
      listItems.splice(listItems.indexOf(currentId), 1);
    }

    const query = listItems
      .slice(0, parseInt(this.dataset.limit))
      .map(id => `id:${id}`)
      .join('%20OR%20');

    return `${this.dataset.url}${query}`;
  }
}
customElements.define('recently-viewed-products', RecentlyViewedProducts);

class ProductRecommendations extends HTMLElement {
  #observer;

  connectedCallback() {
    if (this.__initialized) return;
    this.__initialized = true;

    if (this.dataset.noLoad === 'true' || this.hasAttribute('data-no-load')) return;

    if (this.#hasServerRenderedContent()) {
      this.classList.add('product-recommendations--loaded');
      return;
    }

    if (!this.dataset.url) return;

    this.#initObserver();
  }

  disconnectedCallback() {
    this.#observer?.disconnect?.();
    this.#observer = undefined;
  }

  #hasServerRenderedContent() {
    return !!(
      this.querySelector('slider-component') ||
      this.querySelector('.resource-list__item') ||
      this.querySelector('product-card') ||
      this.querySelector('.product-card') ||
      this.querySelector('.grid__item')
    );
  }

  #initObserver() {
    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.#loadRecommendations(this.dataset.productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#observer.observe(this);
  }

  async #loadRecommendations(productId) {
    const { url, sectionId } = this.dataset;
    try {
      const response = await fetch(`${url}&product_id=${productId}&section_id=${sectionId}`);
      const html = await response.text();
      const temp = document.createElement('div');
      temp.innerHTML = html;

      const recommendations = temp.querySelector('product-recommendations');
      const hasContent = recommendations?.innerHTML.trim().length > 0;

      if (hasContent) {
        this.innerHTML = recommendations.innerHTML;
      } else {
        this.closest('.related-products-wrapper')?.classList.add('hidden');
      }

      if (!this.querySelector('slider-component') && this.classList.contains('complementary-products')) {
        this.remove();
      }

      if (temp.querySelector('.grid__item')) {
        this.classList.add('product-recommendations--loaded');
      }

      const iconWrapper = this.querySelectorAll('icon-wrapper');
      if (iconWrapper) iconWrapper.forEach(iconWrapper => iconWrapper._init());

    } catch (err) {
      console.error('ProductRecommendations load error:', err);
    }
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class BaseQuickProductModal extends HTMLElement {
  constructor() {
    super();
    this.modalContent = null;
    this.openedBy = null;

    this.addEventListener('product-information:loaded', ({ target }) => {
      target.initializeProductSwapUtility?.();
    });
  }

  get modalContentSelector() {
    return '';
  }

  get primaryOpenerSelector() {
    return '';
  }

  get fallbackOpenerSelector() {
    return '';
  }

  get sectionIdDatasetKey() {
    return '';
  }

  get sectionIdSelector() {
    return '';
  }

  get sectionIdAttribute() {
    return '';
  }

  connectedCallback() {
    this.modalContent = this.querySelector(this.modalContentSelector) || this.modalContent;

    if (window.Shopify && Shopify.designMode) {
      const section = this.closest('.shopify-section');
      if (section) {
        section.addEventListener('shopify:section:select', () => {
          const opener = this._findOpener(section);
          if (!opener) {
            const modalComponent = this.closest('modal-component');
            if (modalComponent) modalComponent.isOpen = true;
            return;
          }
          this.show(opener);
        });

        section.addEventListener('shopify:section:deselect', () => {
          const modalComponent = this.closest('modal-component');
          modalComponent?.close?.();
        });
      }
    }
  }

  close(preventFocus = false) {
    const cartNotification = document.querySelector('cart-drawer');
    if (cartNotification) cartNotification.setActiveElement(this.openedBy);

    setTimeout(() => {
      if (this.modalContent) this.modalContent.innerHTML = '';
    }, 500);

    if (preventFocus) this.openedBy = null;

    const modalComponent = this.closest('modal-component');
    modalComponent?.close?.();
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  hide(preventFocus = false) {
    this.close(preventFocus);
  }

  show(opener) {
    if (!opener) return;

    this.openedBy = opener;
    opener.setAttribute('aria-disabled', true);
    opener.classList.add('loading');
    const loadingSpinner = opener.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.remove('hidden');

    if (!this.modalContent) {
      this.modalContent = this.querySelector(this.modalContentSelector) || null;
    }

    const productUrl = opener.getAttribute('data-product-url');
    const requestUrl = this._buildRequestUrl(productUrl, this._getSectionId());

    fetch(requestUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const productElement = responseHTML.querySelector('product-information');
        if (!productElement || !this.modalContent) return;

        this.preprocessHTML(productElement);
        HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
        theme.bindModalToggles?.();

        const modalComponent = this.closest('modal-component');
        const viewDetailsLink = modalComponent?.querySelector('.product__view-details');
        const baseUrl = productElement.dataset.url;
        if (viewDetailsLink && baseUrl) {
          viewDetailsLink.setAttribute('href', baseUrl);
        }

        if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
        if (window.ProductModel) window.ProductModel.loadShopifyXR();

        modalComponent?.show?.(opener);
      })
      .finally(() => {
        opener.removeAttribute('aria-disabled');
        opener.classList.remove('loading');
        const loadingSpinnerDone = opener.querySelector('.loading__spinner');
        if (loadingSpinnerDone) loadingSpinnerDone.classList.add('hidden');
      });
  }

  _findOpener(section) {
    return section.querySelector(this.primaryOpenerSelector) || section.querySelector(this.fallbackOpenerSelector);
  }

  _getSectionId() {
    const fromSelf = this.dataset[this.sectionIdDatasetKey];
    const fromDom = document.querySelector(this.sectionIdSelector)?.getAttribute(this.sectionIdAttribute);
    return fromSelf || fromDom || null;
  }

  _buildRequestUrl(productUrl, sectionId) {
    let requestUrl = productUrl;
    try {
      const urlObj = new URL(productUrl, window.location.origin);
      const params = new URLSearchParams(urlObj.search);
      const currentVariantId = params.get('variant');

      params.delete('section_id');
      params.delete('sections');

      const nextParams = new URLSearchParams();
      if (currentVariantId) nextParams.set('variant', currentVariantId);
      if (sectionId) nextParams.set('section_id', sectionId);

      const qs = nextParams.toString();
      requestUrl = `${urlObj.pathname}${qs ? `?${qs}` : ''}`;
    } catch (e) {
      requestUrl = sectionId ? `${productUrl}?section_id=${sectionId}` : productUrl;
    }
    return requestUrl;
  }

  preprocessHTML(productElement) {
    productElement.classList.forEach((classApplied) => {
      if (classApplied.startsWith('color-') || classApplied === 'gradient') {
        this.modalContent.classList.add(classApplied);
      }
    });
    this.preventDuplicatedIDs(productElement);
    this.removeDOMElements(productElement);
    this.removeGalleryListSemantic(productElement);
    this.updateImageSizes(productElement);
    this.preventVariantURLSwitching(productElement);
  }

  preventVariantURLSwitching(productElement) {
    productElement.setAttribute('data-update-url', 'false');
  }

  removeDOMElements(productElement) {
    const modalDialog = productElement.querySelectorAll('modal-dialog');
    if (modalDialog) modalDialog.forEach((modal) => modal.remove());

    const sideDrawerOpener = productElement.querySelectorAll('side-drawer-opener');
    if (sideDrawerOpener) sideDrawerOpener.forEach((button) => button.remove());

    const sideDrawer = productElement.querySelectorAll('side-drawer');
    if (sideDrawer) sideDrawer.forEach((drawer) => drawer.remove());
  }

  preventDuplicatedIDs(productElement) {
    const sectionId = productElement.dataset.section;
    const oldId = sectionId;
    const newId = `quickadd-${sectionId}`;
    productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
    Array.from(productElement.attributes).forEach((attribute) => {
      if (attribute.value.includes(oldId)) {
        productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
      }
    });
    productElement.dataset.originalSection = sectionId;
  }

  removeGalleryListSemantic(productElement) {
    const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
    if (!galleryList) return;

    galleryList.setAttribute('role', 'presentation');
    galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
  }

  updateImageSizes(productElement) {
    const product = productElement.querySelector('.product');
    const desktopColumns = product?.classList.contains('product--columns');
    if (!desktopColumns) return;

    const mediaImages = product.querySelectorAll('.product__media img');
    if (!mediaImages.length) return;

    let mediaImageSizes =
      '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

    if (product.classList.contains('product--medium')) {
      mediaImageSizes = mediaImageSizes.replace('715px', '605px');
    } else if (product.classList.contains('product--small')) {
      mediaImageSizes = mediaImageSizes.replace('715px', '495px');
    }

    mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
  }
}

if (!customElements.get('quick-view-modal')) {
  class QuickViewModal extends BaseQuickProductModal {
    get modalContentSelector() {
      return '#QuickStandardModal';
    }

    get primaryOpenerSelector() {
      return '.quick-view-button[data-product-url]';
    }

    get fallbackOpenerSelector() {
      return '.quick-add-button[data-product-url]';
    }

    get sectionIdDatasetKey() {
      return 'quickViewSectionId';
    }

    get sectionIdSelector() {
      return '[data-quick-view-section-id]';
    }

    get sectionIdAttribute() {
      return 'data-quick-view-section-id';
    }
  }
  customElements.define('quick-view-modal', QuickViewModal);
}

if (!customElements.get('quick-add-modal')) {
  class QuickAddModal extends BaseQuickProductModal {
    get modalContentSelector() {
      return '#QuickAddStandardModal';
    }

    get primaryOpenerSelector() {
      return '.quick-add-button[data-product-url]';
    }

    get fallbackOpenerSelector() {
      return '.quick-view-button[data-product-url]';
    }

    get sectionIdDatasetKey() {
      return 'quickAddSectionId';
    }

    get sectionIdSelector() {
      return '[data-quick-add-section-id]';
    }

    get sectionIdAttribute() {
      return 'data-quick-add-section-id';
    }
  }
  customElements.define('quick-add-modal', QuickAddModal);
}

class ImageComparison extends HTMLElement {
  constructor() {
    super();
    this.active = false;
    this.button = this.querySelector('button');
    this.currentEvents = {};
    this.stepPercent = 2;
    this.resizeHandler = this.updateDragEvents.bind(this);
    this.startHandlerBound = this.startHandler.bind(this);
    this.onHandlerBound = this.onHandler.bind(this);
    this.endHandlerBound = this.endHandler.bind(this);
    this.keydownHandlerBound = this.keydownHandler.bind(this);
    this.init();
  }

  init() {
    this.updateDragEvents();
    window.addEventListener('resize', this.resizeHandler);
    this.button?.addEventListener('keydown', this.keydownHandlerBound);

    theme.initSectionVisible({
      element: this,
      callback: () => this.animate(),
      threshold: 400
    });
  }

  updateDragEvents() {
    const isMobile = window.innerWidth <= 1024;
    const events = isMobile
      ? { start: 'touchstart', move: 'touchmove', end: 'touchend' }
      : { start: 'mousedown', move: 'mousemove', end: 'mouseup' };

    if (JSON.stringify(this.currentEvents) === JSON.stringify(events)) return;

    this.removeDragEvents();
    this.addDragEvents(events.start, events.move, events.end);
    this.currentEvents = events;
  }

  addDragEvents(startEvent, moveEvent, endEvent) {
    this.button?.addEventListener(startEvent, this.startHandlerBound);
    document.body.addEventListener(moveEvent, this.onHandlerBound, { passive: true });
    document.body.addEventListener(endEvent, this.endHandlerBound);
  }

  removeDragEvents() {
    if (this.currentEvents.start) {
      this.button?.removeEventListener(this.currentEvents.start, this.startHandlerBound);
      document.body.removeEventListener(this.currentEvents.move, this.onHandlerBound);
      document.body.removeEventListener(this.currentEvents.end, this.endHandlerBound);
    }
  }

  disconnectedCallback() {
    this.removeDragEvents();
    window.removeEventListener('resize', this.resizeHandler);
    this.button?.removeEventListener('keydown', this.keydownHandlerBound);
  }

  animate() {
    this.classList.add('animating');

    gsap.timeline({
      scrollTrigger: {
        trigger: this,
        start: 'top center',
      }
    })
      .to(this, { duration: 0, '--percent': '50%' })
      .to(this, {
        duration: 1,
        onComplete: () => this.classList.remove('animating')
      });
  }

  startHandler() { this.active = true; }
  endHandler() { this.active = false; }

  keydownHandler(event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();

    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentPercent = parseFloat(getComputedStyle(this).getPropertyValue('--percent'));
    const currentPercentValue = Number.isNaN(currentPercent) ? 50 : currentPercent;
    const nextPercent = Math.max(0, Math.min(100, currentPercentValue + (direction * this.stepPercent)));
    this.style.setProperty('--percent', `${nextPercent}%`);
  }

  onHandler(e) {
    if (!this.active) return;
    const event = e.touches?.[0] || e;
    const x = event.pageX - this.offsetLeft;

    const distance = this.clientWidth;
    const mouseX = Math.max(0, Math.min(x, distance));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', `${mousePercent}%`);
  }
}
customElements.define('image-comparison', ImageComparison);

if(!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this._intervalId = null;
      this._autoplaySpeed = Number(this.getAttribute('data-autoplay-speed')) || 0;
      if (!this._autoplaySpeed || window.innerWidth <= 749) return;
      this.listItems = this.querySelectorAll('.media-item');
      this.currentIndex = 1;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._startAutoplay();
          } else {
            this._stopAutoplay();
          }
        });
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      this.observer?.disconnect?.();
    }

    _startAutoplay() {
      if (this._intervalId || !this._autoplaySpeed) return;
      this._intervalId = window.setInterval(() => this.addClassToListItems(), this._autoplaySpeed);
    }

    _stopAutoplay() {
      if (!this._intervalId) return;
      window.clearInterval(this._intervalId);
      this._intervalId = null;
    }

    addClassToListItems() {
      this.listItems.forEach(item => {
          item.classList.remove('active');
      });
      this.listItems[this.currentIndex]?.classList.add('active');
      this.currentIndex = (this.currentIndex + 1) % this.listItems.length;
    }
  }
  customElements.define('media-gallery', MediaGallery);
}

class WordBreaker extends HTMLElement {
  constructor() {
    super();
    this._processed = false;
    this.isPaused = false;
    this._scheduled = false;
  }

  connectedCallback() {
    this.type = this.dataset.animateType;
    if (this._processed || this._scheduled || theme.config.motionReduced) return;
    this._scheduled = true;

    this.typeSplitText = this.dataset.typeSplitText || "words";

    const heading = this.querySelectorAll("h1,h2,h3,h4,h5,h6");
    heading.forEach(heading => {
      if (heading && !heading.hasAttribute("aria-label")) {
        heading.setAttribute("aria-label", heading.textContent.trim());
      }
    });

    theme.initSectionVisible({
      element: this,
      threshold: 400,
      callback: () => this._initSplitText(),
    });
  }

  _initSplitText() {
    if (this._processed || theme.config.motionReduced || !this.isConnected) return;
    this._processed = true;

    const run = () => {
      if (!this.isConnected) return;
      const result = SplitText.create(this, {
        type: this.typeSplitText,
        wordsClass: this.dataset.wordsClass || "words",
        linesClass: this.dataset.linesClass || "lines",
        charsClass: this.dataset.charsClass || "chars",
        tag: "span",
        mask: this.dataset.mask || null,
      });

      const words = result.words;
      requestAnimationFrame(() => this.classList.add("is-ready"));
      this.animateWords(words);
    };

    // Wait for fonts to load before creating SplitText
    if (theme.utils && theme.utils.waitForFonts) {
      theme.utils.waitForFonts().then(run).catch(() => setTimeout(run, 200));
    } else {
      setTimeout(run, 200);
    }
  }

  animateWords(word) {
    if (this.isPaused) return;
    switch (this.type) {
      case "slide-up-chars":
        const chars = this.querySelectorAll(".chars");
        const translateSmall = this.dataset.translateSmall || false;
        const translate = translateSmall ? "20%" : "55%";

        let tl = gsap.timeline({
          scrollTrigger: {
            trigger: this,
            start: "top 90%",
            once: true,
          }
        });

        tl.from(chars, {
          y: translate,
          autoAlpha: 0,
          stagger: 0.08,
          duration: 0.6,
          ease: theme.config.gsapEasePower2Out,
        });
        break;
      case "slide-up":
        gsap.set(this, { opacity: 1 });

        gsap.from(word, {
          y: "55%",
          autoAlpha: 0,
          stagger: 0.08,
          duration: 0.6,
          ease: theme.config.gsapEasePower2Out,
          scrollTrigger: {
            trigger: this,
            start: "top 90%",
            once: true,
          }
        });
        break;
      case "reveal":
        const lines = this.querySelectorAll(".lines");

        lines.forEach((el, i) => {
          const words = el.querySelectorAll(".words");
          words.forEach((word) => {
            const animatedWord = document.createElement("span");
            animatedWord.className = "inline-block word w-full h-full z-2 absolute left-0 top-0 will-transform no-select pointer-events-none";
            animatedWord.textContent = word.textContent;
            word.appendChild(animatedWord);
          });

          const mask = el.querySelectorAll(".word");

          let tl = gsap.timeline({
            scrollTrigger: {
              trigger: el,
              start: "top center",
              end: "bottom center",
              scrub: 1
            }
          });

          tl.to(mask, {
            clipPath: "inset(0% 0% 0% 0%)",
            duration: 1,
            stagger: 0.5
          });
        });

        break;
      default:
        break;
    }
  }
}

if (!customElements.get("word-breaker"))
  customElements.define("word-breaker", WordBreaker);

class AnimatedElement extends HTMLElement {
  static ANIMATIONS = {
    "fade-in": {
      from: { opacity: 0, y: 20 },
      to: { opacity: 1, y: 0 }
    }
  };

  static DURATION = 0.6;

  connectedCallback() {
    this.type = this.dataset.animateType;
    if (!this.type || theme.config.motionReduced) return;

    this._setupState();
    this._onInview();
  }

  _setupState() {
    const config = AnimatedElement.ANIMATIONS[this.type];
    if (config) gsap.set(this, config.from);
  }

  _onInview() {
    ScrollTrigger.create({
      trigger: this,
      start: "top 90%",
      onEnter: () => this._runAnimation(),
      once: true
    });
  }

  _runAnimation() {
    const config = AnimatedElement.ANIMATIONS[this.type];
    if (!config) return;

    const delay = parseFloat(this.dataset.animateDelay) || 0;

    gsap.to(this, {
      ...config.to,
      duration: AnimatedElement.DURATION,
      delay,
      ease: theme.config.gsapEasePower2Out
    });
  }
}

if (!customElements.get("animated-element")) {
  customElements.define("animated-element", AnimatedElement);
}

class AnimatedList extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.cacheElements();

    if (theme.config.motionReduced) return;
    this.onInview();
  }

  cacheElements() {
    this.items = this.querySelectorAll('.animate-card')
  }

  setupParams() {
    return {
      duration: 0.5,
      stagger: 0.1,
      delay: 0.1,
    }
  }

  onInview() {
    ScrollTrigger.create({
      trigger: this,
      start: "top bottom",
      onEnter: () => this.animateItems(this.items),
      once: true,
    });
  }

  animateItems(items) {
    const params = this.setupParams();
    gsap.to(items, {
      opacity: 1,
      x: 0,
      ...params
    });
  }
}

if (!customElements.get("animated-list"))
  customElements.define("animated-list", AnimatedList);

class TabListComponent extends HTMLElement {
  constructor() {
    super();

    this.tabs = [];
    this.contents = [];
    this.activeTab = null;
    this.tabListPanel = null;
    this.wrapper = null;
    this.resizeHandler = this.resize.bind(this);
    this.indicatorDelay = 0;
  }

  connectedCallback() {
    this.cacheElements();

    if (this.tabs.length === 0 || !this.wrapper || !this.tabListPanel) {
      console.warn("TabListComponent: Missing required elements (tabs, wrapper, or tabListPanel).");
      return;
    }

    this.indicatorDelay = this.scrollAuto.dataset.delayIndicator || 0;
    // Hide all contents except the first one
    this.setUpTabContents(this.contents);
    // Set wrapper height = first content
    this.setUpHeightWrapper(this.wrapper, this.contents);

    this.activeTab = this.tabs[0];
    this.activeTab.classList.add("active","left-indicator");
    this.updatePosIndicator(this.activeTab);

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.handleTabClick(tab));
    });

    window.addEventListener("resize", this.resizeHandler);

    if(Shopify.designMode) {
      document.addEventListener("shopify:section:select", () => this.resize());
      document.addEventListener("shopify:section:deselect", () => this.resize());
      document.addEventListener("shopify:section:load", () => this.resize());
      document.addEventListener("shopify:section:reorder", () => this.resize());
      document.addEventListener("shopify:block:select", () => this.resize());
      document.addEventListener("shopify:block:deselect", () => this.resize());
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.resizeHandler);
  }

  handleTabClick(tab) {
    this.switchTo(tab);

    setTimeout(() => {
      this.updatePosIndicator(tab);
    }, this.indicatorDelay);
  }

  cacheElements() {
    this.tabs = Array.from(this.querySelectorAll("[data-target]"));
    this.contents = Array.from(this.querySelectorAll('[id^="list-panel-content-"]'));
    this.tabListPanel = this.querySelector("[data-tab-list-panel]");
    this.wrapper = this.querySelector("[data-list-tab-container]");
    this.scrollAuto = this.querySelector("scroll-auto");
    this.smallDelay = 0.28;
    this.duration = 0.35;
    this.largeDuration = 0.38;
    this.stagger = 0.1;
  }

  resize() {
    this.setUpHeightWrapper(this.wrapper, this.contents);
    if (this.activeTab) {
      this.updatePosIndicator(this.activeTab);
    }
  }

  updatePosIndicator(tab) {
    if (!this.tabListPanel || !tab) return;

    const rectContainer = this.tabListPanel.getBoundingClientRect();
    const rectTab = tab.getBoundingClientRect();

    const posIndicator = rectTab.left - rectContainer.left;
    const widthIndicator = rectTab.width;

    this.tabListPanel.style.setProperty("--pos-indicator", `${posIndicator}px`);
    this.tabListPanel.style.setProperty("--width-indicator", `${widthIndicator}px`);
  }

  switchTo(tab) {
    if (tab === this.activeTab) return;

    const prevTab = this.activeTab;
    const targetId = tab.dataset.target;

    const targetContent = this.querySelector(`#${targetId}`);
    const prevContent = this.querySelector(`#${prevTab.dataset.target}`);

    if (!targetContent || !prevContent) return;

    this.updateWrapperHeight(this.wrapper, targetContent);

    gsap.set(targetContent, { position: "static" });
    gsap.set(prevContent, { position: "absolute" });

    this.fadeOut(prevContent);
    this.fadeIn(targetContent);

    this.updateTabClasses(tab, prevTab);

    this.activeTab = tab;
  }

  updateTabClasses(currentTab, prevTab) {
    prevTab.classList.remove("active");
    currentTab.classList.add("active");

    const offset = prevTab.getBoundingClientRect().left - currentTab.getBoundingClientRect().left;
    const isRight = offset > 0;

    const cleanUpClasses = () => {
      prevTab.classList.remove("left-indicator", "left-out-indicator", "right-indicator", "right-out-indicator");
    };

    prevTab.classList.add(isRight ? "right-out-indicator" : "left-out-indicator");
    currentTab.classList.add(isRight ? "right-indicator" : "left-indicator");

    setTimeout(cleanUpClasses, this.indicatorDelay);
  }

  // ------------------------------
  // GSAP ANIMATION HELPERS
  // ------------------------------
  setUpTabContents(contents) {
    if (!contents) return;
    contents.forEach((content, index) => {
      gsap.set(content, {
        autoAlpha: index === 0 ? 1 : 0,
        position: index === 0 ? "static" : "absolute",
        top: 0,
        left: 0,
        width: "100%",
        display: "block",
      });
    });
  }

  setUpHeightWrapper(wrapper, contents) {
    if (!wrapper || !contents || contents.length === 0) return;
    const initialHeight = contents[0].offsetHeight;
    gsap.set(wrapper, {
      minHeight: initialHeight,
      height: initialHeight,
    });
  }

  updateWrapperHeight(wrapper, content) {
    if (!content && !wrapper) return;
    const newHeight = content.offsetHeight;
    gsap.to(wrapper, {
      minHeight: newHeight,
      height: newHeight,
      duration: this.duration,
      ease: theme.config.gsapEasePower2Out,
    });
  }

  fadeOut(content) {
    if (!content) return;
    gsap.to(content, { autoAlpha: 0, duration: this.smallDelay, ease: theme.config.gsapEasePower2Out });
  }

  fadeIn(content) {
    if (!content) return;
    const items = content.querySelectorAll('.animate-card');
    gsap.fromTo(
      content,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        delay: this.smallDelay,
        duration: this.largeDuration,
        ease: theme.config.gsapEasePower2Out,
      }
    );

    if (!items) return;
    const x = theme.media.isMobile ? 30 : 50;
    let tl = gsap.timeline();
    tl.fromTo(items, { autoAlpha: 0, x: x }, { autoAlpha: 1, x: 0, delay: this.smallDelay, duration: this.largeDuration, ease: theme.config.gsapEasePower2Out, stagger: this.stagger });
  }
}

if (!customElements.get("tablist-component"))
  customElements.define("tablist-component", TabListComponent);

class BackgroundAnimated extends HTMLElement {
  constructor() {
    super();

    this.media = null;
    this.type = null;
    this.duration = 0;
    this.start = null;
    this.triggers = [];
    this._designModeAbortController = null;
  }

  _isEventForThisElement(event) {
    const target = event?.target;
    if (!target || !target.contains) return true;
    return target === this || target.contains(this);
  }

  _handleDesignModeEvent(event) {
    if (!this._isEventForThisElement(event)) return;
    this.animated();
  }

  connectedCallback() {
    this.cacheElements();
    if (theme.config.motionReduced || !this.media) return;
    requestAnimationFrame(() => this.classList.add("is-ready"));
    this.animated();
    if (Shopify.designMode) {
      this._designModeAbortController = new AbortController();
      const { signal } = this._designModeAbortController;
      const boundHandler = (e) => this._handleDesignModeEvent(e);
      document.addEventListener("shopify:section:select", boundHandler, { signal });
      document.addEventListener("shopify:section:deselect", boundHandler, { signal });
      document.addEventListener("shopify:section:load", boundHandler, { signal });
      document.addEventListener("shopify:section:reorder", boundHandler, { signal });
      document.addEventListener("shopify:block:select", boundHandler, { signal });
      document.addEventListener("shopify:block:deselect", boundHandler, { signal });
    }
  }

  disconnectedCallback() {
    if (theme.config.motionReduced) return;
    this.triggers.forEach((t) => t.kill());
    this.triggers = [];
    this._designModeAbortController?.abort();
  }

  cacheElements() {
    this.media = this.querySelector(".image-animated");
    this.type = this.dataset.backgroundAnimatedType || null;
    this.duration = this.dataset.backgroundAnimatedDuration || 1.3;
    this.start = this.dataset.backgroundAnimatedStart || "top 90%";
    this.autoRatio = this.dataset.autoRatio || false;
  }

  animated() {
    if (this.type === 'none') return;
    this.triggers.forEach((t) => t.kill());
    this.triggers = [];
    switch (this.type) {
      case "parallax":
        this.parallax();
        break;
      case "parallax-horizontal":
        this.parallaxHorizontal();
        break;
      case "zoom-out":
        this.zoomOut();
        break;
      case "zoom-in":
        this.zoomIn();
        break;
      case "zoom-on-scroll":
        this.zoomOnScroll();
        break;
      case "layer":
        this.layer();
        break;
    }
  }

  zoomOut() {
    const tween = gsap.to(this.media, { scale: 1, opacity: 1, transformOrigin: "top", duration: this.duration, ease: "power4.inOut", scrollTrigger: { trigger: this, start: this.start, invalidateOnRefresh: true }});
    this.triggers.push(tween);
  }

  zoomIn() {
    const tween = gsap.to(this.media, { scale: 1, opacity: 1, transformOrigin: "center", duration: this.duration, ease: "power4.inOut", scrollTrigger: { trigger: this, start: this.start, invalidateOnRefresh: true }});
    this.triggers.push(tween);
  }

  zoomOnScroll() {
    const tween = gsap.fromTo(
      this.media,
      { scale: 1, transformOrigin: "center bottom" },
      {
        scale: 1.5, transformOrigin: "center bottom", ease: "none", scrollTrigger: { trigger: this, scrub: true, invalidateOnRefresh: true }
      }
    );
    this.triggers.push(tween);
  }

  parallax() {
    const speed = Number(this.dataset.backgroundAnimatedSpeed || 1.2);
    const n = this.media.offsetHeight - this.parentElement.offsetHeight;

    let yValue = n * speed;
    yValue = theme.media.isMobile ? yValue / 2 : yValue;

    const tween = gsap.fromTo(
      this.media,
      { y: -yValue },
      {
        y: yValue, ease: "none", scrollTrigger: { trigger: this, end: "bottom top", scrub: !0, invalidateOnRefresh: true }
      }
    );
    this.triggers.push(tween);
  }

  parallaxHorizontal() {
    if (theme.media.isMobile || theme.config.motionReduced) return;
    const speed = Number(this.dataset.backgroundAnimatedSpeed || 1.2);
    const n = this.media.offsetWidth - this.offsetWidth;
    const PERCENTAGE = 0.25;

    let xValue = n * speed * PERCENTAGE;
    let yValue = n * speed * PERCENTAGE;

    xValue = theme.media.isMobile ? xValue / 2 : xValue;
    yValue = theme.media.isMobile ? yValue / 2 : yValue;

    gsap.set(this.media, {
      xPercent: -50,
      yPercent: -50
    });

    const tween = gsap.fromTo(
      this.media,
      { x: xValue, y: yValue },
      { x: -xValue, y: -yValue, ease: "none", scrollTrigger: { trigger: this, scrub: 1.5, invalidateOnRefresh: true} }
    );

    window.addEventListener("gallery:progress", (event) => {
      const progress = event.detail.progress;
      tween.progress(progress);
    });
    this.triggers.push(tween);
  }

  layer() {
    const layerFirst = this.querySelector(".layer-first");
    const layerSecond = this.querySelector(".layer-second");
    if (!layerFirst || !layerSecond) return;

    const section = this.closest(".section");
    const wordBreaker = section?.querySelector("word-breaker");
    const delay = 0.3;
    const layersAnimate = { clipPath: "inset(0 0 100% 0)", delay: delay, duration: this.duration, ease: theme.config.gsapEasePower2Out };

    // Pause word breaker if it exists
    wordBreaker && (wordBreaker.isPaused = true);

    __deferToDomReady(() => {
      this._animateLayer(layerFirst, layerSecond, this.media, layersAnimate, wordBreaker);
    });
  }

  // helper for animate layer
  _animateLayer(layerFirst, layerSecond, media, layersAnimate, wordBreaker) {
    let resumed = false;
    let tl = gsap.timeline({
      onUpdate: () => {
        if (!resumed && tl.progress() > 0.8) {
          resumed = true;
          this._resumeWordBreaker(wordBreaker);
          this.classList.add("is-animated");
        }
      }
    });

    tl.to(layerFirst, layersAnimate)
      .to(layerSecond, layersAnimate, "<")
      .fromTo(
        media,
        { scale: 1.2, autoAlpha: 0.5 },
        { scale: 1,
          autoAlpha: 1,
          duration: this.duration + 0.5,
          ease: theme.config.gsapEasePower2Out
        },
        "<="
      );

    if (theme.media.isDesktop) {
      const tween = gsap.fromTo(
        this,
        { y: 0 },
        {
          y: this.offsetHeight / 2, ease: "none", scrollTrigger: { trigger: this, start: "top top", end: "bottom top", scrub: true, invalidateOnRefresh: true }
        }
      );
      this.triggers.push(tween);
    }

    this.triggers.push(tl);
  }

  /* helper */
  _resumeWordBreaker(wordBreaker) {
    if (!wordBreaker) return;

    wordBreaker.isPaused = false;

    if (!wordBreaker.words) {
      if (wordBreaker.splitTextInstance?.words) {
        wordBreaker.words = wordBreaker.splitTextInstance.words;
      } else {
        const result = SplitText.create(wordBreaker, {
          type: wordBreaker.typeSplitText || "words",
          wordsClass: wordBreaker.dataset.wordsClass || "words",
          linesClass: wordBreaker.dataset.linesClass || "lines",
          charsClass: wordBreaker.dataset.charsClass || "chars",
          tag: "span",
          mask: wordBreaker.dataset.mask || null
        });
        wordBreaker.words = result.words;
      }
    }

    wordBreaker.animateWords(wordBreaker.words);
  }
}
if (!customElements.get("background-animated"))
customElements.define("background-animated", BackgroundAnimated);

class ProductCardMedia extends HTMLElement {
  static DURATION = 0.4;
  static CLIP_FULL = "inset(0% 0% 0% 0%)";
  static CLIP_LEFT = "inset(0 100% 0 0)";
  static CLIP_RIGHT = "inset(0 0 0 100%)";

  constructor() {
    super();
    this.primaryImage = null;
    this.secondImage = null;
    this._onMouseEnter = (e) => this._handleHover(e, true);
    this._onMouseLeave = (e) => this._handleHover(e, false);
  }

  connectedCallback() {
    this._cacheElements();
    if (!this._isHoverEnabled()) return;

    this._beforeHover();
    this.addEventListener("mouseenter", this._onMouseEnter);
    this.addEventListener("mouseleave", this._onMouseLeave);
  }

  disconnectedCallback() {
    this.removeEventListener("mouseenter", this._onMouseEnter);
    this.removeEventListener("mouseleave", this._onMouseLeave);
  }

  _isHoverEnabled() {
    return !theme.config.motionReduced &&
      !theme.media.isTablet &&
      !theme.media.isTouch &&
      !!this.secondImage;
  }

  _cacheElements() {
    this.primaryImage = this.querySelector(".product-card__media--primary");
    this.secondImage = this.querySelector(".product-card__media--secondary");
  }

  _beforeHover() {
    gsap.to(this.secondImage, {
      opacity: 1,
      clipPath: ProductCardMedia.CLIP_LEFT,
      duration: ProductCardMedia.DURATION,
      ease: theme.config.gsapEasePower2Out
    });
  }

  _isMouseOnLeft(event) {
    if (!(event instanceof MouseEvent)) return false;
    const rect = this.getBoundingClientRect();
    return event.clientX - rect.left < rect.width / 2;
  }

  _getGsapConfig() {
    return {
      duration: ProductCardMedia.DURATION,
      ease: theme.config.gsapEasePower2Out
    };
  }

  _handleHover(event, entering) {
    const isLeft = this._isMouseOnLeft(event);
    const clipSecondary = isLeft ? ProductCardMedia.CLIP_LEFT : ProductCardMedia.CLIP_RIGHT;
    const clipPrimary = isLeft ? ProductCardMedia.CLIP_RIGHT : ProductCardMedia.CLIP_LEFT;
    const config = this._getGsapConfig();

    if (entering) {
      gsap.fromTo(
        this.secondImage,
        { opacity: 1, clipPath: clipSecondary, scale: 1.2 },
        { opacity: 1, clipPath: ProductCardMedia.CLIP_FULL, scale: 1, ...config }
      );
      gsap.fromTo(
        this.primaryImage,
        { opacity: 1, clipPath: ProductCardMedia.CLIP_FULL },
        { opacity: 1, clipPath: clipPrimary, ...config }
      );
    } else {
      gsap.fromTo(
        this.secondImage,
        { opacity: 1, clipPath: ProductCardMedia.CLIP_FULL },
        { opacity: 1, clipPath: clipSecondary, ...config }
      );
      gsap.fromTo(
        this.primaryImage,
        { opacity: 1, clipPath: clipPrimary, scale: 1.2 },
        { opacity: 1, clipPath: ProductCardMedia.CLIP_FULL, scale: 1, ...config }
      );
    }
  }
}

if (!customElements.get("product-card-media")) {
  customElements.define("product-card-media", ProductCardMedia);
}

class ProductCard extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector("[data-product-card-reveal-container]");
  }

  connectedCallback() {
    if (!theme.media.isDesktopLarge || theme.media.isTouch) return;

    this.beforeHoverContentHeight();
    this.addEventListener("mouseenter", () => this._onEnter(true));
    this.addEventListener("mouseleave", () => this._onEnter(false));
    this.addEventListener("focusin", () => this._onEnter(true));
    this.addEventListener("focusout", () => this._onEnter(false));
  }

  disconnectedCallback() {
    if (!theme.media.isDesktopLarge || theme.media.isTouch) return;

    this.removeEventListener("mouseenter", () => this._onEnter(true));
    this.removeEventListener("mouseleave", () => this._onEnter(false));
    this.removeEventListener("focusin", () => this._onEnter(true));
    this.removeEventListener("focusout", () => this._onEnter(false));
  }

  checkRevealContent() {
    const isRevealContent = this.dataset.productCardRevealContent === "height";
    return isRevealContent;
  }

  initPropsContentHeight() {
    gsap.set(this.container, { height: "0" });
  }

  beforeHoverContentHeight() {
    if (!this.checkRevealContent() || !this.container) return;
    this.initPropsContentHeight();
  }

  _onEnter(entering) {
    if (!this.checkRevealContent() || !this.container) return;

    if (this._hoverLockTween) {
      this._hoverLockTween.kill();
      this._hoverLockTween = null;
    }

    this._hoverLockTween = gsap.to(this.container, {
      height: entering ? this.container.scrollHeight + "px" : "0",
      duration: entering ? 0.8 : 0.6,
      ease: entering ? "power4.inOut" : "expo.inOut",
      onComplete: () => {
        this._hoverLockTween = null;
      },
    });
  }
}
if (!customElements.get("product-card"))
customElements.define("product-card", ProductCard);

if (!customElements.get("button-hover")) {
  class ButtonHover extends HTMLElement {
    constructor() {
      super();

      this._onEnter = this._onEnter.bind(this);
      this._onMove  = this._onMove.bind(this);
      this._onMouseEnter = (event) => this._onEnter(event, true);
      this._onMouseLeave = (event) => this._onEnter(event, false);
    }

    connectedCallback() {
      if (theme.config.motionReduced || theme.media.isTouch || theme.media.isMobile) return;
      this._cacheElements();

      this.addEventListener("mouseenter", this._onMouseEnter);
      this.addEventListener("mouseleave", this._onMouseLeave);
      this.addEventListener("mousemove", this._onMove);
    }

    disconnectedCallback() {
      if (!this.button) return;

      this.removeEventListener("mouseenter", this._onMouseEnter);
      this.removeEventListener("mouseleave", this._onMouseLeave);
      this.removeEventListener("mousemove", this._onMove);
    }

    _cacheElements() {
      this.button = this;
      this.mouse = { x: 0, y: 0, lastX: 0, lastY: 0 };
      this.movement = Number(this.dataset.buttonHoverMovement || 20);
      this.layerInner = this.querySelector(".layer__inner");
      this.isHovering = false;
      this.currentAnimation = null;
    }

    _onEnter(event, entering) {
      this.classList.add("on-hover");
      const isTop = this._detectMousePosition(event);
      const clipPathDefault = "-50%";
      const dir = isTop ? "-150%" : "100%";
      const dirPrimary = isTop ? "-150%" : "100%";
      const classHover = isTop ? "on-hover-top" : "on-hover-bottom";

      if (entering) {
        this.isHovering = true;
        this.classList.add(classHover);
        if (this.layerInner) {
          gsap.fromTo(
            this.layerInner,
            { y: dir },
            {
              y: clipPathDefault,
              duration: 0.3,
              ease: theme.config.gsapEasePower2Out,
            }
          );
        }
      } else {
        this.isHovering = false;
        if (this.currentAnimation) {
          this.currentAnimation.kill();
          this.currentAnimation = null;
        }
        this.classList.remove("on-hover", "on-hover-top", "on-hover-bottom");
        if (this.layerInner) {
        gsap.fromTo(
          this.layerInner,
          { y: clipPathDefault },
            { y: dirPrimary, duration: 0.3, ease: theme.config.gsapEasePower2Out }
          );
        }
        gsap.to(this.button, {duration: 0.3, x: 0, y:0});
      }
    }

    _onMove(e) {
      const rect = this.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      this.mouse.vx = mx - this.mouse.lastX;
      this.mouse.vy = my - this.mouse.lastY;
      this.mouse.lastX = mx;
      this.mouse.lastY = my;

      this._parallax(mx, my, rect);
    }

    _parallax(mx, my, rect) {
      if (!this.isHovering) return;

      const speed = Math.hypot(this.mouse.vx * 2 + this.mouse.vy * 2);

      if (this.currentAnimation) this.currentAnimation.kill();

      this.currentAnimation = gsap.to(this, {
        duration: 0.25,
        x: ((mx - rect.width / 2) / rect.width) * (this.movement + speed * 0.3),
        y: ((my - rect.height / 2) / rect.height) * (this.movement + speed * 0.3),
        ease: theme.config.gsapEasePower2Out,
        onComplete: () => {
          this.currentAnimation = null;
        }
      });
    }

    _detectMousePosition(event) {
      let isTop = false;
      if (event && event instanceof MouseEvent) {
        const rect = this.getBoundingClientRect();
        isTop = event.clientY - rect.top < rect.height / 2;
      }
      return isTop;
    }
  }
  customElements.define("button-hover", ButtonHover);
}

class MarqueeComponent extends HTMLElement {
  constructor() {
    super();

    this.tl = null;
    this.paused = false;
    this.timeScale = 1;

    // drag state
    this.dragging = false;
    this.startPos = 0;
    this.startProgress = 0;

    // scroll scrub state
    this._scrub = { value: 1 };
    this._scrubTween = null;

    // bind
    this._onHoverIn = () => this.pause(true);
    this._onHoverOut = () => this.pause(false);
    this._onClick = () => this.togglePause();

    this._onHoverIn = this._onHoverIn.bind(this);
    this._onHoverOut = this._onHoverOut.bind(this);

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
  }

  connectedCallback() {
    this._cacheElements();
    if (!this.content || !this.itemsWrapper) return;

    this._cloneUntilFull();
    this._duplicateWrapper();
    this._initMarquee();
  }

  disconnectedCallback() {
    this.tl?.kill();
    ScrollTrigger.getAll().forEach(t => t.kill());

    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    window.removeEventListener('pointermove', this._onPointerMove);
  }

  /**
   * Cache elements
   */
  _cacheElements() {
    this.content = this.querySelector('.section-marquee__content');
    this.itemsWrapper = this.querySelector('[marquee-element="panel"]');
    this.dragging = this.getAttribute('dragging') === 'true';
  }

  /**
   * Duplicate wrapper for looping
   */
  _duplicateWrapper() {
    this.content.appendChild(this.itemsWrapper.cloneNode(true));
  }

  /**
   * Clone items until full
   */
  _cloneUntilFull() {

    const containerSize = this.itemsWrapper.offsetWidth;

    const first = this.itemsWrapper.firstElementChild;
    if (!first) return;

    const itemSize = first.offsetWidth;

    const copies = Math.ceil(containerSize / itemSize);

    for (let i = 0; i < copies; i++) {
      this.itemsWrapper.appendChild(first.cloneNode(true));
    }
  }

  /**
   * Marquee logic
   */
  _initMarquee() {
    const speed = theme.utils.getAttr(100, this.getAttribute('speed')) / 5;
    const scrollScrub = theme.utils.getAttr(false, this.getAttribute('scroll-scrub'));
    const gap = theme.utils.getAttr(0, this.getAttribute('data-gap'));
    const move = -100;
    const dragging = theme.utils.getAttr(false, this.getAttribute('dragging'));
    const hoverPause = theme.utils.getAttr(false, this.getAttribute('hover-pause'));

    const size = this.itemsWrapper.offsetWidth;

    const duration = size / speed;

    this.tl = gsap.timeline({
      repeat: -1,
      onReverseComplete: () => this.tl.progress(1)
    });

    this.tl.fromTo(
      this.content.children,
      { xPercent: 0, x: gap },
      {
        xPercent: move,
        x: -gap,
        duration,
        ease: 'none'
      }
    );

    if (scrollScrub && theme.media.isDesktop) {
      this._initScrollScrub();
    }

    if (theme.media.isDesktop && !dragging && hoverPause) {
      this.addEventListener('mouseenter', this._onHoverIn);
      this.addEventListener('mouseleave', this._onHoverOut);
    }
    if (theme.media.isDesktop && dragging && !hoverPause) {
      this._bindDrag();
    }
  }

  /**
   * Initialize scroll scrub
   */
  _initScrollScrub() {
    const setTimeScale = gsap.quickSetter(this.tl, 'timeScale');

    ScrollTrigger.create({
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        if (this.paused || this.dragging) return;

        const velocity = gsap.utils.clamp(
          -60,
          60,
          self.getVelocity() * 0.006
        );

        const target = velocity || this.timeScale;

        if (!this._scrubTween) {
          this._scrubTween = gsap.to(this._scrub, {
            value: target,
            duration: 0.4,
            ease: 'power2.out',
            onUpdate: () => setTimeScale(this._scrub.value)
          });
        } else {
          this._scrubTween.vars.value = target;
          this._scrubTween.invalidate().restart();
        }
      }
    });
  }

  /**
   * Pause control
   */
  pause(state) {
    this.paused = state;

    gsap.to(this.tl, {
      timeScale: state ? 0 : this.timeScale,
      duration: 0.4,
      ease: 'power2.out'
    });

    this.classList.toggle('is-paused', state);
  }

  togglePause() {
    this.pause(!this.paused);
  }

  /**
   * Drag
   */
  _bindDrag() {
    this.style.touchAction = 'none';
    this.classList.add('is-draggable');

    this.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  }

  /**
   * Pointer down
   */
  _onPointerDown(e) {

    this.dragging = true;
    this._pauseTween?.kill();
    this._scrubTween?.kill();
    this._scrubTween = null;

    this.tl.timeScale(0);

    this.startPos = e.clientX;
    this.startProgress = this.tl.progress();

    this.classList.add('is-grabbing');
    window.addEventListener('pointermove', this._onPointerMove);
  }

  /**
   * Pointer move
   */
  _onPointerMove(e) {
    if (!this.dragging) return;

    const current = e.clientX;
    const delta = (current - this.startPos) / window.innerWidth;

    let next = this.startProgress - delta;
    this.tl.progress(gsap.utils.wrap(0, 1, next));
  }

  /**
   * Pointer up
   */
  _onPointerUp() {
    if (!this.dragging) return;

    this.dragging = false;

    gsap.to(this.tl, {
      timeScale: this.timeScale,
      duration: 0.4,
      ease: 'power2.out'
    });

    this.classList.remove('is-grabbing');
    window.removeEventListener('pointermove', this._onPointerMove);
  }
}

customElements.define('marquee-component', MarqueeComponent);

class IconWrapper extends HTMLElement {
  constructor() {
    super();
    this._processed = false;
  }

  connectedCallback() {
    if (theme.config.motionReduced || theme.media.isTouch) return;

    __deferToDomReady(() => this._init());

    if (Shopify.designMode && !IconWrapper.__designModeBound) {
      IconWrapper.__designModeBound = true;
      const reinitAll = () => {
        document.querySelectorAll('icon-wrapper').forEach((el) => el?._init?.());
      };
      document.addEventListener('shopify:section:load', reinitAll);
      document.addEventListener('shopify:section:reorder', reinitAll);
    }
  }

  _init() {
    this._cacheElements();
    this._cloneIcons();
  }

  _cacheElements() {
    this.iconWrappers = this.querySelectorAll("[data-icon-wrapper]");
  }

  _cloneIcons() {
    if (this._processed) return;
    this._processed = true;
    this.iconWrappers.forEach((iconWrapper) => {
      iconWrapper.appendChild(iconWrapper.querySelector("svg").cloneNode(true));
    });
  }
}
if (!customElements.get("icon-wrapper")) {
  customElements.define("icon-wrapper", IconWrapper);
}

const setLabelWidth = (scope = document) => {
  const fields = scope.querySelectorAll(".field");
  fields.forEach((field) => {
    const label = field.querySelector(".field__label");
    if (!label) return;

    const width = Math.ceil(
      label.getBoundingClientRect().width || label.offsetWidth || label.scrollWidth || 0
    );
    if (width > 0) {
      field.style.setProperty("--label-width", `${width}px`);
    }
  });
};

const scheduleSetLabelWidth = (scope = document) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setLabelWidth(scope);
    });
  });
};

const togglePassword = () => {
  const fields = document.querySelectorAll(".field--password");
  fields && fields.forEach((field) => {
    const passwordInput = field.querySelector('input[type="password"]');
    const passwordToggle = field.querySelector('[data-password-toggle]');
    passwordToggle.addEventListener('click', (e) => {
      e.preventDefault();
      passwordInput.type = passwordInput.type === 'password' ? 'text' : 'password';
      passwordToggle.classList.toggle('is-active');
    });
  });
};

class MotionTexts extends HTMLElement {
  static SPLIT_TEXT_CONFIG = {
    type: "words, chars",
    charsClass: "chars",
    tag: "span",
    mask: "chars"
  };

  static MAX_TRANSLATE_Y = 100;
  static EASE = "power4.inOut";

  connectedCallback() {
    if (this._scheduled || this._initialized) return;
    this._scheduled = true;

    theme.initSectionVisible({
      element: this,
      threshold: 400,
      callback: () => this._init(),
    });
  }

  _init() {
    if (this._initialized || theme.config.motionReduced || !this.isConnected) return;
    this._initialized = true;
    this._cacheElements();
  }

  _cacheElements() {
    const texts = this.querySelectorAll(".motion-texts__text");
    this.primary = texts[0];
    this.secondary = texts[1];

    if (!this.primary || !this.secondary) return;

    const initSplitAndAnimate = () => {
      if (!this.isConnected) return;
      const split = SplitText.create(this.primary, MotionTexts.SPLIT_TEXT_CONFIG);
      requestAnimationFrame(() => this.classList.add("is-ready"));
      this._animateText(split.masks);
    };

    if (theme.utils?.waitForFonts) {
      theme.utils.waitForFonts().then(initSplitAndAnimate);
    } else {
      setTimeout(initSplitAndAnimate, 200);
    }
  }

  _animateText(masks) {
    const { primary, secondary } = this;
    const heightSec = Math.min(MotionTexts.MAX_TRANSLATE_Y, secondary.offsetHeight);

    gsap.set(primary, { y: heightSec });

    const tl = gsap.timeline();
    const ease = MotionTexts.EASE;

    tl.fromTo(masks, { autoAlpha: 0 }, {
      autoAlpha: 1,
      duration: 1,
      stagger: 0.08,
      ease
    });

    tl.to(primary, {
      y: 0,
      duration: 0.5,
      ease
    }, "-=0.5");

    tl.fromTo(secondary, { y: heightSec, autoAlpha: 0 }, {
      y: 0,
      autoAlpha: 1,
      duration: 1,
      ease
    }, "-=0.8");

    tl.to(primary, { clearProps: "transform, autoAlpha" });
    tl.to(secondary, { clearProps: "transform, autoAlpha" });
  }
}
if (!customElements.get("motion-texts")) {
  customElements.define("motion-texts", MotionTexts);
}

class ParallaxOnHover extends HTMLElement {
  static PARALLAX_FACTOR = 0.25;
  static GSAP_CONFIG = { duration: 0.3, ease: "power3.out" };

  constructor() {
    super();
    this._onMove = (e) => this._animate(e);
    this._onEnter = () => this.hoverTarget?.addEventListener("mousemove", this._onMove);
    this._onLeave = () => this.hoverTarget?.removeEventListener("mousemove", this._onMove);
  }

  connectedCallback() {
    if (theme.config.motionReduced || theme.media.isTouch) return;

    this._cacheElements();
    if (!this.image) return;

    this._setupAnimate();

    this.hoverTarget = this.closest(".content-on--image")
    ? this.closest(".content-on--image")
    : this;

    this.hoverTarget?.addEventListener("pointerenter", this._onEnter);
    this.hoverTarget?.addEventListener("pointerleave", this._onLeave);
  }

  disconnectedCallback() {
    this.hoverTarget?.removeEventListener("pointerenter", this._onEnter);
    this.hoverTarget?.removeEventListener("pointerleave", this._onLeave);
    this.hoverTarget?.removeEventListener("mousemove", this._onMove);
  }

  _cacheElements() {
    this.image = this.querySelector(".image-animated");
  }

  _setupAnimate() {
    gsap.set(this.image, {
      left: "50%",
      top: "50%",
      xPercent: -50,
      yPercent: -50
    });

    const config = ParallaxOnHover.GSAP_CONFIG;
    this.xTo = gsap.quickTo(this.image, "x", config);
    this.yTo = gsap.quickTo(this.image, "y", config);
  }

  _animate(event) {
    const rect = this.getBoundingClientRect(),
          offsetDiff = this.image.offsetWidth - this.offsetWidth,
          factor = ParallaxOnHover.PARALLAX_FACTOR,

          x = ((event.clientX - rect.left) / rect.width - 0.5) * offsetDiff * factor,
          y = ((event.clientY - rect.top) / rect.height - 0.5) * offsetDiff * factor;

    this.xTo(x);
    this.yTo(y);
  }
}

if (!customElements.get("parallax-on-hover")) {
  customElements.define("parallax-on-hover", ParallaxOnHover);
}

if (!customElements.get("article-testimonials")) {
  class AnimationCircle extends HTMLElement {
    constructor() {
      super();
      this._onMouseMove = this._onMouseMove.bind(this);
      this._rafId = 0;
      this._lastPointer = null;
      this._setCircleTransform = null;
      this._xTo = null;
      this._yTo = null;
    }

    connectedCallback() {
      this._cacheElements();
      this._inview();
      this.animationCircle();
    }

    disconnectedCallback() {
      document.removeEventListener("mousemove", this._onMouseMove);
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = 0;
      }
    }

    _cacheElements() {
      this.circle = this.querySelector(".testimonials-wrapper .image-block");
      this.image = this.circle.querySelector("img");
      this.article = this.querySelector(".article-wrapper");
      this.testimonials = this.querySelector(".testimonials-wrapper-content");
      this.trigger = this.querySelector(".section-content-wrapper");
    }

    animationCircle() {
      if (!this.circle || !this.image) return;
      this._setCircleTransform = gsap.quickSetter(this.circle, "--transform");
      this._xTo = gsap.quickTo(this.image, "x", { duration: 0.8, ease: "power2.out" });
      this._yTo = gsap.quickTo(this.image, "y", { duration: 0.8, ease: "power2.out" });
      document.addEventListener("mousemove", this._onMouseMove);
    }

    _onMouseMove(e) {
      this._lastPointer = e;
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = 0;
        this._renderPointerAnimation();
      });
    }

    _renderPointerAnimation() {
      if (!this._lastPointer || !this._setCircleTransform || !this._xTo || !this._yTo) return;
      const strength = 20;
      const { innerWidth, innerHeight } = window;
      const deltaX = (this._lastPointer.clientX - innerWidth / 2) / strength;
      const deltaY = (this._lastPointer.clientY - innerHeight / 2) / strength;
      this._setCircleTransform(`translate(${deltaX}px, ${deltaY}px)`);
      this._xTo(-deltaX * 0.5);
      this._yTo(-deltaY * 0.5);
    }

    _inview() {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: this.querySelector(".section-content-wrapper"),
          start: "top center",
          end: "bottom bottom",
          once: true,
        },
      });
      tl.fromTo(this.circle, {
        "--width": "200vw",
      }, {
        "--width": "0",
        duration: 1,
        ease: "power2.out",
      })
      .from(this.image, {
        autoAlpha: 0,
        duration: 1,
        ease: "power2.out",
      },"-=0.1")
      .from(this.article, {
        y: 100,
        autoAlpha: 0,
        duration: 1,
        ease: "power2.out",
      },"-=0.5")
      .from(this.testimonials, {
        autoAlpha: 0,
        y: 100,
        duration: 1,
        ease: "power2.out",
      },"-=0.5");
    }
  }
  customElements.define("article-testimonials", AnimationCircle);
}

class CollectionLoadMore extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.items = [];
    this.limit = 0;
    this.currentIndex = 0;
  }

  connectedCallback() {
    const collectionList = this.closest("collection-list") || document.querySelector("collection-list.collection-list");
    if (!collectionList) return;

    this.limit = Number(collectionList.dataset.limit) || 8;
    this.items = [...collectionList.querySelectorAll('.resource-list__item')];
    this.button = collectionList.querySelector('.pagination-button-wrapper button');
    this.spinner = this.button.querySelector('.loading__spinner')

    this.currentIndex = this._getCurrentIndex();

    if (this._isDone()) {
      this._hideButton();
      return;
    }

    this.button?.addEventListener('click', this._onLoadMore.bind(this));
  }

  _getCurrentIndex() {
    const hiddenIndex = this.items.findIndex(item =>
      item.classList.contains('none')
    );
    return hiddenIndex > -1 ? hiddenIndex : this.items.length;
  }

  _onLoadMore(e) {
    e.preventDefault();

    this._loadingButton();
    const firstNewIndex = this.currentIndex;
    const nextIndex = this.currentIndex + this.limit;
    let revealedAny = false;

    setTimeout(() => {
      for (let i = this.currentIndex; i < nextIndex && i < this.items.length; i++) {
        this._showItem(this.items[i]);
        revealedAny = true;
      }

      this._loadedButton();
      ScrollTrigger.getAll().forEach(st => st.refresh(true));
      this.currentIndex = nextIndex;

      if (revealedAny) {
        this._focusFirstLoadedItem(firstNewIndex);
      }

      if (this._isDone()) {
        this._hideButton();
      }
    }, 1000);
  }

  _focusFirstLoadedItem(startIndex) {
    const item = this.items[startIndex];
    if (!item) return;

    let focusTarget = null;

    if (typeof theme !== 'undefined' && theme.getFocusableElements) {
      const candidates = theme
        .getFocusableElements(item)
        .filter(
          (el) =>
            !el.classList.contains('hidden') && el.getAttribute('aria-disabled') !== 'true'
        );
      focusTarget = candidates[0] || null;
    }

    if (!focusTarget) {
      if (!item.hasAttribute('tabindex')) {
        item.setAttribute('tabindex', '-1');
      }
      focusTarget = item;
    }

    requestAnimationFrame(() => {
      focusTarget?.focus?.({ preventScroll: false });
    });
  }

  _loadingButton() {
    this.button?.classList.add('loading');
    this.spinner?.classList.remove('hidden');
  }

  _loadedButton() {
    this.button?.classList.remove('loading');
    this.spinner?.classList.add('hidden');
  }

  _showItem(item) {
    item.classList.remove('none');

    item.querySelectorAll('background-animated').forEach(el => {
      this._reconnectElement(el);
    });
  }

  _reconnectElement(el) {
    const parent = el.parentNode;
    if (!parent) return;

    const placeholder = document.createElement('div');
    parent.insertBefore(placeholder, el);
    parent.removeChild(el);
    placeholder.replaceWith(el);

    el.connectedCallback?.();
  }

  _isDone() {
    return this.currentIndex >= this.items.length;
  }

  _hideButton() {
    this.button?.parentElement && (this.button.parentElement.style.display = 'none');
  }
}

if (!customElements.get('collection-load-more')) {
  customElements.define('collection-load-more', CollectionLoadMore);
}

window.addEventListener('load', () => {
  ScrollTrigger.sort();
  ScrollTrigger.refresh();
});

document.addEventListener("DOMContentLoaded", () => {
  setLabelWidth();
  togglePassword();

  document.addEventListener("change", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('.recipient-form .recipient-checkbox input[type="checkbox"]')) return;

    const recipientForm = target.closest("recipient-form");
    scheduleSetLabelWidth(recipientForm || document);
  });
});

class LazyContent extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-hydrated')) return;

    const trigger = this.getAttribute('data-trigger') || 'interaction';

    if (trigger === 'interaction') {
      const events = ['mousemove', 'touchstart', 'scroll', 'keydown'];
      const handler = () => {
        this.hydrate();
        events.forEach(e => window.removeEventListener(e, handler));
      };
      events.forEach(e => window.addEventListener(e, handler, { once: true, passive: true, capture: true }));
    } else {
      this.hydrate();
    }
  }

  hydrate() {
	if (this.hasAttribute('data-hydrated')) return;
	const template = this.querySelector('template');
    if (template) {
      this.appendChild(template.content.cloneNode(true));
      this.setAttribute('data-hydrated', '');
    }
  }
}
customElements.define('lazy-content', LazyContent);

if (!customElements.get('custom-cookie-banner')) {
  class CustomCookieBanner extends HTMLElement {
    constructor() {
      super();
      this.allowBtn = this.querySelector('[data-action="allow"]');
      this.declineBtn = this.querySelector('[data-action="decline"]');

      this.handleAllow = this.handleAllow.bind(this);
      this.handleDecline = this.handleDecline.bind(this);
    }

    connectedCallback() {
      const init = () => {
        if (window.Shopify?.loadFeatures) {
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
            if (error) {
              this.remove();
              return;
            }
            this.checkConsentStatus();
          });
        } else {
          this.checkConsentStatus();
        }
      };

      init();
    }

    disconnectedCallback() {
      this.allowBtn?.removeEventListener('click', this.handleAllow);
      this.declineBtn?.removeEventListener('click', this.handleDecline);
      if (this.observer) this.observer.disconnect();
    }

    checkConsentStatus() {
      const privacyExists = window.Shopify && window.Shopify.customerPrivacy;
      let shouldShow = false;

      if (privacyExists) {
        const trackingConsent = window.Shopify.customerPrivacy.getTrackingConsent();
        if (trackingConsent === 'no_interaction' || trackingConsent === 'unknown') {
          shouldShow = true;
        }
      } else {
        shouldShow = true;
      }

      if (!shouldShow && document.getElementById('shopify-pc__banner')) {
        shouldShow = true;
      }

      if (shouldShow) {
        this.allowBtn?.addEventListener('click', this.handleAllow, { once: true });
        this.declineBtn?.addEventListener('click', this.handleDecline, { once: true });
        this.classList.add('is-visible');
        this.showBanner();
      } else {
        this.remove();
      }
    }

    showBanner() {
      if (typeof gsap !== 'undefined') {
        gsap.fromTo(this,
          { yPercent: 120, opacity: 0 },
          {
            yPercent: 0,
            opacity: 1,
            duration: 0.8,
            ease: "back.out(1.2)",
            delay: 3,
            onComplete: () => gsap.set(this, { clearProps: "all" })
          }
        );
      } else {
        this.style.transform = 'translateY(0)';
        this.style.opacity = '1';
      }
    }

    hideBanner() {
      if (typeof gsap !== 'undefined') {
        gsap.to(this, {
          yPercent: 120,
          opacity: 0,
          duration: 0.6,
          ease: "power2.inOut",
          onComplete: () => this.remove()
        });
      } else {
        this.remove();
      }
    }

    handleAllow() {
      const nativeAcceptBtn = document.getElementById('shopify-pc__banner__btn-accept');
      if (nativeAcceptBtn) {
        nativeAcceptBtn.click();
        this.hideBanner();
      } else if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent(true, () => this.hideBanner());
      } else {
        this.hideBanner();
      }
    }

    handleDecline() {
      const nativeDeclineBtn = document.getElementById('shopify-pc__banner__btn-decline');
      if (nativeDeclineBtn) {
        nativeDeclineBtn.click();
        this.hideBanner();
      } else if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent(false, () => this.hideBanner());
      } else {
        this.hideBanner();
      }
    }
  }

  customElements.define('custom-cookie-banner', CustomCookieBanner);
}

if (!customElements.get('product-model')) {
  customElements.define(
    'product-model',
    class ProductModel extends DeferredMedia {
      constructor() {
        super();
      }

      connectedCallback() {
        super.connectedCallback();
        // Ensure play button works even if base class binding is flaky or timing issue
        const playButton = this.querySelector('[ref="deferredMediaPlayButton"]');
        if (playButton) {
          playButton.addEventListener('click', () => {
            if (!this.getAttribute('loaded')) {
              this.loadContent();
            }
          });
        }
      }

      loadContent() {
        super.loadContent();

        Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: this.setupModelViewerUI.bind(this),
          },
        ]);
      }

      setupModelViewerUI(errors) {
        if (errors) return;

        this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'));
      }
    }
  );
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel) window.ProductModel.loadShopifyXR();
});

const sectionRenderCache = new Map();

class SectionFetcher extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this._abortController = new AbortController();
    const { signal } = this._abortController;

    const activateMode = this.dataset.activate || 'inview';
    if (activateMode === 'interaction') {
      this._cleanupTrigger = theme.initWhenVisible(() => this.initialize(), null);
    } else {
      this._cleanupTrigger = this._initWhenInView();
    }

    document.addEventListener('section:cached', this._onSectionCached.bind(this), { signal });
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) return;
      this._cleanupTrigger?.();
      this._cleanupTrigger = null;
      this._abortController?.abort();
    });
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get targetElementId() {
    return this.dataset.targetId;
  }

  initialize() {
    if (this._fetchStarted) return;
    this._fetchStarted = true;

    const targetElement = document.getElementById(this.targetElementId);
    if (!targetElement || targetElement.hasAttribute('data-loaded')) return;

    const targetUrl = new URL(window.location.href);
    targetUrl.searchParams.set('section_id', this.sectionId);
    const url = targetUrl.toString();

    if (sectionRenderCache.has(url)) {
      this._renderFromCache(url);
    } else {
      this._fetchAndRender(url);
    }
  }

  _renderFromCache(url) {
    const cachedHTML = sectionRenderCache.get(url);
    if (cachedHTML) this._updateDOM(cachedHTML);
  }

  async _fetchAndRender(url) {
    sectionRenderCache.set(url, '');

    try {
      const response = await fetch(url, { signal: this._abortController?.signal });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const htmlText = await response.text();

      sectionRenderCache.set(url, htmlText);
      this._updateDOM(htmlText);

      document.dispatchEvent(
        new CustomEvent('section:cached', {
          detail: { url, sectionId: this.sectionId },
        })
      );
    } catch (error) {
      sectionRenderCache.delete(url);
      if (error.name !== 'AbortError') {
        console.error('[SectionFetcher]', error);
      }
    }
  }

  _updateDOM(htmlText) {
    const oldSection = document.getElementById(this.targetElementId);
    if (!oldSection || oldSection.hasAttribute('data-loaded')) return;

    const parser = new DOMParser();
    const newDocument = parser.parseFromString(htmlText, 'text/html');
    const newSection = newDocument.getElementById(this.targetElementId);

    if (!newSection) {
      console.warn(`[SectionFetcher] Target #${this.targetElementId} not found in fetched section`);
      return;
    }

    oldSection.replaceWith(newSection);
    newSection.setAttribute('data-loaded', 'true');
    this.dispatchEvent(
      new CustomEvent('section-fetcher:loaded', {
        bubbles: true,
        detail: {
          container: newSection,
          sectionId: this.sectionId,
          targetId: this.targetElementId,
        },
      })
    );
  }

  _onSectionCached(event) {
    const detail = event.detail;
    if (!detail || detail.sectionId !== this.sectionId) return;
    this._renderFromCache(detail.url);
  }

  _initWhenInView() {
    if (typeof IntersectionObserver === 'undefined') {
      this.initialize();
      return () => {};
    }

    let triggered = false;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (triggered || !entry.isIntersecting) continue;
        triggered = true;
        observer.unobserve(entry.target);
        observer.disconnect();
        this.initialize();
      }
    });

    observer.observe(this);
    return () => observer.disconnect();
  }
}

if (!customElements.get('section-fetcher')) {
  customElements.define('section-fetcher', SectionFetcher);
}
